import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, Paper, Grid, Chip, IconButton, Button, Avatar,
  CircularProgress, Tooltip, Divider, Badge, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  Tab, Tabs, useTheme, useMediaQuery, List, ListItem, ListItemAvatar,
  ListItemText, ListItemSecondaryAction, Menu, InputAdornment, Stack,
  FormControlLabel, Checkbox,
} from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PetsIcon from '@mui/icons-material/Pets';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FilterListIcon from '@mui/icons-material/FilterList';
import StorefrontIcon from '@mui/icons-material/Storefront';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CakeIcon from '@mui/icons-material/Cake';
import WcIcon from '@mui/icons-material/Wc';
import { addDocument, getCollection, updateDocument, findDocumentsByField } from '../services/firestoreService';
import { createBooking, getConsultationPrice, getConsultationArancel, getAvailableSlots, notifyNextWaitlistEntry } from '../services/bookingService';
import { crearPresupuestoAutomatico, resolverConvenio } from '../services/presupuestoAutoService';
import { trackMetaEventServer } from '../services/metaPixel';
import { registrarVentaOrden } from '../services/ventaService';
import { netoDesdeBruto } from '../utils/finance';
import { normalizeRut, validateRut } from '../utils/validators';
import { toLocalDateStr, isScheduleActiveOnDate } from '../utils/dateUtils';
import { useBranch } from '../context/BranchContext';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, where, serverTimestamp } from 'firebase/firestore';
import PaymentDialog from '../components/PaymentDialog';
import PatientOwnerDialog from '../components/PatientOwnerDialog';
import SearchableSelect from '../components/SearchableSelect';

// ── Constants ──────────────────────────────────────────────────────────────────
const WORK_HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8-19
const HOUR_HEIGHT = 60; // altura compacta por hora para mayor densidad

const calcAge = (birthdate) => {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  const now   = new Date();
  const ms    = now - birth;
  const years = Math.floor(ms / (365.25 * 864e5));
  if (years >= 1) return `${years} año${years > 1 ? 's' : ''}`;
  const months = Math.floor(ms / (30.44 * 864e5));
  if (months >= 1) return `${months} mes${months > 1 ? 'es' : ''}`;
  const days = Math.floor(ms / 864e5);
  return `${days} día${days !== 1 ? 's' : ''}`;
};

const PROF_COLORS = [
  { bg: '#3B82F6', light: '#EFF6FF', text: '#1D4ED8' },
  { bg: '#10B981', light: '#ECFDF5', text: '#065F46' },
  { bg: '#F59E0B', light: '#FFFBEB', text: '#92400E' },
  { bg: '#8B5CF6', light: '#F5F3FF', text: '#5B21B6' },
  { bg: '#EF4444', light: '#FEF2F2', text: '#991B1B' },
  { bg: '#06B6D4', light: '#ECFEFF', text: '#155E75' },
  { bg: '#EC4899', light: '#FDF2F8', text: '#9D174D' },
  { bg: '#84CC16', light: '#F7FEE7', text: '#3F6212' },
];

const STATUS_CONFIG = {
  no_confirmado: { label: 'No confirmado', color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, bg: '#FFFBEB', border: '#F59E0B' },
  confirmed:  { label: 'Confirmado',  color: 'info',    icon: <CheckCircleIcon    fontSize="small" />, bg: '#EFF6FF', border: '#3B82F6' },
  pending:    { label: 'Pendiente',   color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, bg: '#FFFBEB', border: '#F59E0B' },
  en_sala:    { label: 'En sala',     color: 'success', icon: <CheckCircleIcon    fontSize="small" />, bg: '#D1FAE5', border: '#10B981' },
  completed:  { label: 'Atendido',   color: 'success', icon: <CheckCircleIcon    fontSize="small" />, bg: '#ECFDF5', border: '#10B981' },
  cancelled:  { label: 'Cancelado',  color: 'error',   icon: <CancelIcon         fontSize="small" />, bg: '#FEF2F2', border: '#EF4444' },
  paciente_cancelo: { label: 'Paciente canceló', color: 'error', icon: <CancelIcon fontSize="small" />, bg: '#FEF2F2', border: '#EF4444' },
  deleted:    { label: 'Eliminado',  color: 'default', icon: <ClearIcon          fontSize="small" />, bg: '#F9FAFB', border: '#9CA3AF' },
  no_show:    { label: 'No asistió', color: 'default', icon: <EventBusyIcon      fontSize="small" />, bg: '#F9FAFB', border: '#9CA3AF' },
};

const APPT_TYPE_CONFIG = {
  primera_consulta: { label: 'Primera vez',  color: '#4F46E5', bg: '#EEF2FF' },
  control:          { label: 'Control',       color: '#0F6E56', bg: '#E1F5EE' },
  vacuna:           { label: 'Vacuna',        color: '#4D7C0F', bg: '#F7FEE7' },
  urgencia:         { label: 'Urgencia',      color: '#991B1B', bg: '#FEE2E2' },
  telemedicina:     { label: 'Telemedicina',  color: '#1D4ED8', bg: '#EFF6FF' },
};

const STATUS_ACTIONS = [
  { to: 'confirmed', label: 'Confirmado',   color: 'info'    },
  { to: 'completed', label: 'Atendido',     color: 'success' },
  { to: 'no_show',   label: 'No asistió',   color: 'default' },
  { to: 'paciente_cancelo', label: 'Cancelado por paciente', color: 'error' },
  { to: 'deleted', label: 'Eliminar',     color: 'error'   },
];

const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DAY_NAMES_LONG  = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTH_NAMES     = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// ── Helpers ────────────────────────────────────────────────────────────────────
const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (d, n) => {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
};

const addMonths = (d, n) => {
  const r = new Date(d); r.setMonth(r.getMonth() + n); return r;
};

const startOfWeek = (d) => {
  const r = new Date(d);
  const day = r.getDay();
  r.setDate(r.getDate() - (day === 0 ? 6 : day - 1));
  return r;
};

const formatDate = (d) =>
  `${DAY_NAMES_LONG[d.getDay()]} ${d.getDate()} de ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;

const formatDateShort = (d) =>
  `${DAY_NAMES_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`;

const formatPrice = (amount) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);

// ── WaitlistDialog ────────────────────────────────────────────────────────────
const WaitlistDialog = ({ open, onClose, slot, professional, date, clinicId, specialtyId, capacityId, userData }) => {
  const [ownerRut, setOwnerRut]       = useState('');
  const [ownerName, setOwnerName]     = useState('');
  const [ownerCelular, setOwnerCelular] = useState('');
  const [petName, setPetName]         = useState('');
  const [petSpecies, setPetSpecies]   = useState('');
  const [searching, setSearching]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const reset = () => {
    setOwnerRut(''); setOwnerName(''); setOwnerCelular('');
    setPetName(''); setPetSpecies(''); setError('');
  };

  const handleSearch = async () => {
    if (!ownerRut) return;
    setSearching(true);
    try {
      const found = await findDocumentsByField('owners', 'rut', ownerRut);
      if (found?.length > 0) {
        setOwnerName(found[0].name || '');
        setOwnerCelular(found[0].celular || '');
      } else {
        setError('Tutor no encontrado. Completa los datos manualmente.');
      }
    } catch { setError('Error buscando tutor'); }
    finally { setSearching(false); }
  };

  const handleSave = async () => {
    if (!ownerName.trim() || !ownerCelular.trim() || !petName.trim() || !petSpecies) {
      setError('Completa nombre del tutor, celular, nombre y especie de la mascota'); return;
    }
    setSaving(true);
    try {
      await addDocument('waitlistEntries', {
        professionalId: professional?.id || '',
        professionalName: `${professional?.nombre || ''} ${professional?.apellidos || ''}`,
        date, slotTime: slot?.time || '',
        clinicId: clinicId || '', specialtyId: specialtyId || '', capacityId: capacityId || '',
        ownerName, ownerCelular, ownerRut,
        petName, petSpecies,
        addedAt: new Date(),
        addedBy: userData?.name || userData?.email || '',
        status: 'waiting',
        notifiedAt: null,
      });
      reset(); onClose('success');
    } catch { setError('Error al guardar en lista de espera'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: '#F59E0B', px: 3, py: 2 }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>Lista de Espera</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          {slot?.time} · Dr/a. {professional?.nombre} {professional?.apellidos}
        </Typography>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {error && <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }} onClose={() => setError('')}>{error}</Alert>}
        <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">TUTOR</Typography></Divider>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField label="RUT" value={ownerRut} size="small" fullWidth placeholder="12345678-9"
            onChange={e => setOwnerRut(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}/>
          <Button variant="outlined" onClick={handleSearch} disabled={searching} sx={{ minWidth: 80 }}
            startIcon={searching ? <CircularProgress size={14}/> : <SearchIcon/>}>Buscar</Button>
        </Stack>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={12}>
            <TextField label="Nombre del tutor *" value={ownerName} size="small" fullWidth required
              onChange={e => setOwnerName(e.target.value)}/>
          </Grid>
          <Grid size={12}>
            <TextField label="Celular *" value={ownerCelular} size="small" fullWidth required
              onChange={e => setOwnerCelular(e.target.value)}/>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">MASCOTA</Typography></Divider>
        <Grid container spacing={1.5}>
          <Grid size={12}>
            <TextField label="Nombre mascota *" value={petName} size="small" fullWidth required
              onChange={e => setPetName(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><PetsIcon fontSize="small"/></InputAdornment> }}/>
          </Grid>
          <Grid size={12}>
            <TextField select label="Especie *" value={petSpecies} size="small" fullWidth required
              onChange={e => setPetSpecies(e.target.value)}>
              {['Canino','Felino','Ave','Conejo','Roedor','Reptil','Otro'].map(s =>
                <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant="outlined" onClick={() => { reset(); onClose(); }} sx={{ borderRadius: 3 }}>Cancelar</Button>
        <Box sx={{ flex: 1 }}/>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 3, bgcolor: '#F59E0B', '&:hover': { bgcolor: '#D97706' } }}>
          {saving ? <CircularProgress size={18} sx={{ color: 'white' }}/> : 'Agregar a lista'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const Scheduler = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData, currentUser } = useAuth();
  const isVeterinario = userData?.role === 'Médico Veterinario';
  const isDirectorMedico = userData?.role === 'Director Médico';
  const isMedico = isVeterinario || isDirectorMedico;

  const [view, setView] = useState('day');       // 'day' | 'week' | 'month' | 'list'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [branches, setBranches] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [capacities, setCapacities] = useState([]);
  const [presupuestosMap, setPresupuestosMap] = useState({});
  const [loading, setLoading] = useState(true);
  const { activeBranchId, isAllBranches, isAdmin, accessibleBranches, filterByBranch } = useBranch();

  // Filters
  const [filterBranch, setFilterBranch] = useState('');
  const [filterClinic, setFilterClinic] = useState('');
  const [filterProf, setFilterProf] = useState('');
  const [filterSpec, setFilterSpec] = useState('');
  const [filterCapacity, setFilterCapacity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [daySearch, setDaySearch] = useState('');

  // Sync branch filter with global active branch (except médicos y directores, cuya sucursal se bloquea abajo)
  useEffect(() => {
    if (isVeterinario || isDirectorMedico) return;
    if (isAllBranches) {
      setFilterBranch(isAdmin ? '' : (accessibleBranches[0]?.id || ''));
    } else {
      setFilterBranch(activeBranchId || '');
    }
  }, [activeBranchId, isAllBranches, isAdmin, accessibleBranches, isVeterinario, isDirectorMedico]);

  // Lock professional filter for Médico Veterinario role
  useEffect(() => {
    if (isVeterinario && userData?.professionalId) {
      setFilterProf(userData.professionalId);
    }
  }, [isVeterinario, userData?.professionalId]);

  // Derive the médico's own branch from their professional's clinics.
  // Prefer the global active branch when it belongs to the professional; otherwise the
  // first assigned/accessible branch that matches one of their clinics.
  const medicoBranchId = useMemo(() => {
    if (!isMedico) return '';
    if (isDirectorMedico) return (userData?.branchIds?.length ? userData.branchIds[0] : (userData?.branchId || activeBranchId || accessibleBranches[0]?.id || ''));
    const assignedBranchIds = (userData?.branchIds?.length ? userData.branchIds : accessibleBranches.map((b) => b.id));
    const prof = professionals.find((p) => p.id === userData?.professionalId);
    const clinicIds = prof ? (prof.clinicIds?.length ? prof.clinicIds : (prof.clinicId ? [prof.clinicId] : [])) : [];
    const profBranchIds = [];
    for (const cid of clinicIds) {
      const clinic = clinics.find((c) => c.id === cid);
      if (clinic?.branchIds?.length) profBranchIds.push(...clinic.branchIds);
      else if (clinic?.branchId) profBranchIds.push(clinic.branchId);
    }
    // Keep the active branch if the professional works there
    if (activeBranchId && activeBranchId !== 'all' && profBranchIds.includes(activeBranchId)) return activeBranchId;
    // Find the first assigned/accessible branch associated with the professional
    const match = assignedBranchIds.find((b) => profBranchIds.includes(b));
    if (match) return match;
    return activeBranchId || assignedBranchIds[0] || accessibleBranches[0]?.id || '';
  }, [isMedico, isDirectorMedico, professionals, clinics, userData?.professionalId, userData?.branchIds, activeBranchId, accessibleBranches]);

  // Lock branch filter to the médico's own branch
  useEffect(() => {
    if ((isVeterinario || isDirectorMedico) && medicoBranchId) {
      setFilterBranch(medicoBranchId);
    }
  }, [isVeterinario, isDirectorMedico, medicoBranchId]);

  // Schedule & blocks
  const [rawSchedules, setRawSchedules] = useState([]);
  const [allBlocks, setAllBlocks] = useState([]);

  // Manual booking dialog
  const [bookDlg, setBookDlg] = useState({ open: false, date: null, time: '', profId: '', profName: '', specialtyId: '', specialtyName: '' });
  const [dlgOwner, setDlgOwner] = useState({ name: '', rut: '', celular: '', mail: '', convenio: '' });
  const [dlgOwnerId, setDlgOwnerId] = useState(null);
  const [dlgOwnerFound, setDlgOwnerFound] = useState(false);
  const [dlgOwnerPets, setDlgOwnerPets] = useState([]);
  const [dlgPetsLoading, setDlgPetsLoading] = useState(false);
  const [dlgPetMode, setDlgPetMode] = useState('new');
  const [dlgSelectedPet, setDlgSelectedPet] = useState(null);
  const [dlgPetData, setDlgPetData] = useState({ name: '', species: '', breed: '', birthDate: '', genero: '' });
  const [dlgBreeds, setDlgBreeds]   = useState([]);
  const [dlgRutSearching, setDlgRutSearching] = useState(false);
  const [dlgSaving, setDlgSaving] = useState(false);
  const [dlgError, setDlgError] = useState('');

  // Payment dialog for manual bookings
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payingBooking, setPayingBooking] = useState(null);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailWeight, setDetailWeight] = useState('');
  const [detailWeightSaving, setDetailWeightSaving] = useState(false);
  const [detailNotas, setDetailNotas] = useState('');
  const [detailNotasSaving, setDetailNotasSaving] = useState(false);
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [editPatientInitial, setEditPatientInitial] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // List view search
  const [searchQuery, setSearchQuery] = useState('');

  // Quick-pick new cita dialog
  const [quickPickOpen, setQuickPickOpen] = useState(false);
  const [quickPickDate, setQuickPickDate] = useState('');
  const [quickPickTime, setQuickPickTime] = useState('');
  const [quickPickProf, setQuickPickProf] = useState('');
  const [quickPickSobrecupo, setQuickPickSobrecupo] = useState(false);

  // Reagendar dialog
  const [reagendarOpen, setReagendarOpen] = useState(false);
  const [reagendarDate, setReagendarDate] = useState('');
  const [reagendarTime, setReagendarTime] = useState('');
  const [reagendarSlots, setReagendarSlots] = useState([]);
  const [reagendarLoading, setReagendarLoading] = useState(false);

  // Reasignar médico dialog
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignProfId, setReassignProfId] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);

  // Tick for current-time line (updates every minute)
  const [tick, setTick] = useState(0);

  // Drag & drop
  const [dragBooking, setDragBooking] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null); // `${profId}_${hour}` or `${profId}_${slotTime}`

  // Waitlist
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistDlgOpen, setWaitlistDlgOpen] = useState(false);
  const [waitlistDlgData, setWaitlistDlgData] = useState({ slot: null, professional: null });

  // New booking dialog fields
  const [dlgAppointmentType, setDlgAppointmentType] = useState('');
  const [dlgDurationMin, setDlgDurationMin]         = useState(20);
  const [dlgMotivoConsulta, setDlgMotivoConsulta]   = useState('');
  const [dlgInternalNotes, setDlgInternalNotes]     = useState('');
  const [dlgNotas, setDlgNotas]                     = useState('');
  const [dlgNoShowCount, setDlgNoShowCount]         = useState(0);
  const [dlgTotalBookings, setDlgTotalBookings]     = useState(0);
  const [dlgSearchMode, setDlgSearchMode]           = useState('rut');
  const [dlgSearchPhone, setDlgSearchPhone]         = useState('');

  // Ref for scrolling to current time
  const dayViewRef = useRef(null);

  // Professional color map
  const profColorMap = useMemo(() => {
    const map = {};
    professionals.forEach((p, i) => { map[p.id] = PROF_COLORS[i % PROF_COLORS.length]; });
    return map;
  }, [professionals]);

  const [owners, setOwners] = useState([]);
  const [patients, setPatients] = useState([]);
  const ownersMap = useMemo(() => Object.fromEntries(owners.map((o) => [o.id, o])), [owners]);
  const patientsMap = useMemo(() => Object.fromEntries(patients.map((p) => [p.id, p])), [patients]);
  const conveniosMap = useMemo(() => {
    const map = {};
    convenios.forEach((c) => {
      if (c.id) map[c.id] = c;
      if (c.name) map[c.name] = c;
    });
    return map;
  }, [convenios]);

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      getCollection('clinics'),
      getCollection('branches').catch(() => []),
      getCollection('professionals'),
      getCollection('specialties'),
      getCollection('capacities'),
      getCollection('convenios').catch(() => []),
      getCollection('breeds').catch(() => []),
      getCollection('owners').catch(() => []),
      getCollection('patients').catch(() => []),
    ]).then(([clinicsData, branchesData, profs, specs, caps, conveniosData, breedsData, ownersData, patientsData]) => {
      setClinics(clinicsData.filter((c) => c.active !== false));
      setBranches(branchesData.filter((b) => b.active !== false));
      setProfessionals(profs.filter((p) => p.active !== false));
      setSpecialties(specs.filter((s) => s.active !== false));
      setCapacities(caps.filter((c) => c.type === 'consulta'));
      setConvenios(conveniosData.filter((c) => c.isActive !== false));
      setDlgBreeds(breedsData || []);
      setOwners(ownersData.filter((o) => o.active !== false) || []);
      setPatients(patientsData.filter((p) => p.active !== false) || []);
    });
  }, []);

  // Load professional schedules whenever the list changes
  useEffect(() => {
    if (professionals.length === 0) return;
    getCollection('professionalSchedules').then((allSchedules) => {
      const toIsoDate = (v) => {
        if (!v) return '';
        if (typeof v === 'string') return v.split('T')[0];
        return toLocalDateStr(v);
      };
      setRawSchedules(allSchedules.map((s) => ({ ...s, startDate: toIsoDate(s.startDate), endDate: toIsoDate(s.endDate) })));
    });
  }, [professionals]);

  // Active schedules for the currently viewed day/week (allows future/past agendas)
  const { profSchedules, scheduleBranchMap } = useMemo(() => {
    const map = {};
    const branchMap = {};
    const selectedStr = toDateStr(selectedDate);
    const ws = toDateStr(startOfWeek(selectedDate));
    const we = toDateStr(addDays(startOfWeek(selectedDate), 6));
    rawSchedules.forEach((s) => {
      if (!s.professionalId || !s.clinicId || !s.specialtyId || !s.capacityId || !s.schedule) return;
      const startStr = s.startDate;
      const endStr = s.endDate;
      let isValid = false;
      if (view === 'day') {
        isValid = !!startStr && selectedStr >= startStr && (!endStr || selectedStr <= endStr)
          && isScheduleActiveOnDate(s, selectedStr);
      } else if (view === 'week') {
        const rangeEnd = endStr || '9999-12-31';
        isValid = !!startStr && ws <= rangeEnd && we >= startStr;
        // Para semana, verificar si al menos un día de la semana es activo según recurrencia
        if (isValid && s.recurrenceType && s.recurrenceType !== 'continuous') {
          let anyDayActive = false;
          for (let i = 0; i < 7; i++) {
            const dayStr = toDateStr(addDays(startOfWeek(selectedDate), i));
            if (isScheduleActiveOnDate(s, dayStr)) { anyDayActive = true; break; }
          }
          isValid = anyDayActive;
        }
      } else if (view === 'month') {
        const monthStart = `${selectedStr.slice(0, 7)}-01`;
        const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
        const monthEnd = toDateStr(nextMonth);
        const rangeEnd = endStr || '9999-12-31';
        isValid = !!startStr && monthStart <= rangeEnd && monthEnd >= startStr;
        // Para mes, verificar si al menos un día del mes es activo según recurrencia
        if (isValid && s.recurrenceType && s.recurrenceType !== 'continuous') {
          let anyDayActive = false;
          const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
          for (let i = 1; i <= daysInMonth; i++) {
            const dayStr = `${selectedStr.slice(0, 7)}-${String(i).padStart(2, '0')}`;
            if (isScheduleActiveOnDate(s, dayStr)) { anyDayActive = true; break; }
          }
          isValid = anyDayActive;
        }
      } else {
        isValid = true;
      }
      if (isValid) {
        const key = `${s.professionalId}_${s.clinicId}_${s.specialtyId}_${s.capacityId}`;
        map[key] = s.schedule;
        branchMap[key] = s.branchId || null;
      }
    });
    return { profSchedules: map, scheduleBranchMap: branchMap };
  }, [rawSchedules, selectedDate, view]);

  // Subscribe to schedule blocks (real-time)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'scheduleBlocks'), (snap) => {
      setAllBlocks(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    });
    return () => unsub();
  }, []);

  // Subscribe to waitlist entries (real-time)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'waitlistEntries'), (snap) => {
      setWaitlistEntries(filterByBranch(snap.docs.map(d => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    setLoading(true);
    // B3: Ocultar estados de pago huérfanos en TODAS las citas (no solo online)
    const HIDE_STATUSES = new Set(['payment_failed', 'expired', 'pending_payment', 'deleted']);
    const q = query(collection(db, 'onlineBookings'), orderBy('date', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Se cargan todas las citas; el filtro por sucursal se aplica en el useMemo `filtered`.
      setBookings(rows.filter((b) => !HIDE_STATUSES.has(b.status)));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  // Subscribe to onlineBooking presupuestos for payment display on cards
  useEffect(() => {
    const q = query(collection(db, 'presupuestos'), where('orderType', '==', 'onlineBooking'));
    const unsub = onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        const p = { id: d.id, ...d.data() };
        map[p.id] = p;
        if (p.orderId) map[p.orderId] = p;
        if (p.orderNumber) map[p.orderNumber] = p;
      });
      setPresupuestosMap(map);
    });
    return () => unsub();
  }, []);

  // Helper to show the right payment label for online (Webpay) vs agenda (presupuesto)
  const getBookingPaymentInfo = useCallback((booking) => {
    const presupuesto = presupuestosMap[booking.bookingCode];
    const hasWebpay = Number(booking.tbk_amount) > 0;
    const patient = patientsMap[booking.patientId || booking.petId];
    const owner = ownersMap[booking.ownerId] || ownersMap[patient?.ownerId] || {};
    const rawConvenio =
      booking.ownerConvenio ||
      booking.convenioName ||
      booking.convenio ||
      owner.convenio ||
      owner.convenioName ||
      patient?.convenio ||
      patient?.convenioName ||
      '';
    const convenioObj = rawConvenio ? (conveniosMap[rawConvenio] || { name: rawConvenio }) : null;
    const convenioName = convenioObj?.name || rawConvenio || '';
    if (presupuesto && !hasWebpay) {
      const item = (presupuesto.items && presupuesto.items[0]) || {};
      const amount = Number(presupuesto.total) || 0;
      const unit = Number(item.precioUnitario) || 0;
      const prestacion = item.prestacion || presupuesto.prestacion || booking.prestacion || booking.specialtyName || '';
      const paid = presupuesto.estado === 'cobrado';
      return {
        hasPayment: true,
        isWebpay: false,
        amount,
        unit,
        prestacion,
        paid,
        label: paid ? 'Pagado' : 'Por pagar',
      };
    }
    if (hasWebpay) {
      const amount = Number(booking.tbk_amount || booking.amount) || 0;
      const paid = booking.paymentStatus === 'pagado';
      return {
        hasPayment: true,
        isWebpay: true,
        amount,
        paid,
        auth: booking.tbk_authorization_code || booking.authorizationCode,
        label: paid ? 'Pagado' : 'Por pagar',
      };
    }
    if (convenioName) {
      return {
        hasPayment: true,
        isWebpay: false,
        isSinCosto: true,
        amount: 0,
        unit: 0,
        prestacion: 'Cita sin costo',
        paid: true,
        label: 'Sin costo',
        convenioName,
      };
    }
    const amount = Number(booking.amount) || 0;
    if (amount > 0) {
      const paid = booking.paymentStatus === 'pagado';
      return {
        hasPayment: true,
        isWebpay: false,
        amount,
        paid,
        label: paid ? 'Pagado' : 'Por pagar',
      };
    }
    return { hasPayment: false };
  }, [presupuestosMap, ownersMap, patientsMap, conveniosMap]);

  // ── Filtered clinics by branch (must come before professionals memos) ───────────
  const filteredClinics = useMemo(() => {
    if (!filterBranch) return clinics;
    return clinics.filter((c) => c.branchIds?.includes(filterBranch));
  }, [clinics, filterBranch]);

  // ── Schedule lookup map: (professionalId, specialtyId) → clinicId ──────────────
  // Used to enrich existing bookings whose clinicId was incorrectly saved from the
  // professional's profile instead of the matching schedule.
  const scheduleClinicMap = useMemo(() => {
    const map = new Map();
    rawSchedules.forEach((s) => {
      if (s.professionalId && s.specialtyId && s.clinicId) {
        const key = `${s.professionalId}_${s.specialtyId}`;
        if (!map.has(key)) map.set(key, s.clinicId);
      }
    });
    return map;
  }, [rawSchedules]);

  // ── Enriched bookings: fix incorrect clinicId using schedule lookup ───────────
  const enrichedBookings = useMemo(() => {
    if (!scheduleClinicMap.size) return bookings;
    return bookings.map((b) => {
      if (!b.professionalId || !b.specialtyId) return b;
      const correctClinicId = scheduleClinicMap.get(`${b.professionalId}_${b.specialtyId}`);
      if (correctClinicId && correctClinicId !== b.clinicId) {
        return { ...b, clinicId: correctClinicId };
      }
      return b;
    });
  }, [bookings, scheduleClinicMap]);

  // ── Filtered capacities (Consulta) by branch, ascending number ────────────────
  const filteredCapacities = useMemo(() => {
    const active = capacities.filter((c) => c.active !== false);
    if (!filterBranch) return active;
    return active
      .filter((c) => c.branchIds?.includes(filterBranch))
      .sort((a, b) => Number(a.number || 0) - Number(b.number || 0));
  }, [capacities, filterBranch]);

  // Reset capacity filter if selected capacity does not belong to the current branch
  useEffect(() => {
    if (filterCapacity && !filteredCapacities.some((c) => c.id === filterCapacity)) {
      setFilterCapacity('');
    }
  }, [filterCapacity, filteredCapacities]);

  // ── ¿El horario (schedule key) pertenece a la sucursal filtrada? ────────────────
  // Usa el branchId real del horario; si es legado (sin branchId), cae al mapeo clínica→sucursal.
  const scheduleMatchesBranch = useCallback((key, cid) => {
    if (!filterBranch) return true;
    const sb = scheduleBranchMap[key];
    if (sb) return sb === filterBranch;
    const clinic = clinics.find((c) => c.id === cid);
    const cids = clinic?.branchIds || (clinic?.branchId ? [clinic.branchId] : []);
    return cids.includes(filterBranch);
  }, [filterBranch, scheduleBranchMap, clinics]);

  // ── Professional options for toolbar dropdown (cascades from sucursal→clínica) ──
  const profDropdownOptions = useMemo(() => {
    let result = professionals;
    if (filterClinic) {
      result = result.filter((p) => p.clinicIds?.includes(filterClinic) || p.clinicId === filterClinic);
    } else if (filterBranch) {
      const ids = filteredClinics.map((c) => c.id);
      result = result.filter((p) => ids.some((cid) => p.clinicIds?.includes(cid) || p.clinicId === cid));
    }
    return result;
  }, [professionals, filterClinic, filterBranch, filteredClinics]);

  // ── Filtered professionals (day-view columns, schedule logic) ──────────────────
  const filteredProfessionals = useMemo(() => {
    let result = professionals;
    if (filterProf) {
      // If a specific professional is selected (locked for médicos), always show them;
      // the schedule itself will enforce the branch/clinic filters.
      result = result.filter((p) => p.id === filterProf);
    } else {
      if (filterClinic) {
        result = result.filter((p) => p.clinicIds?.includes(filterClinic) || p.clinicId === filterClinic);
      } else if (filterBranch) {
        const ids = filteredClinics.map((c) => c.id);
        result = result.filter((p) => ids.some((cid) => p.clinicIds?.includes(cid) || p.clinicId === cid));
      }
    }
    return result;
  }, [professionals, filterClinic, filterProf, filterBranch, filteredClinics]);

  // ── Filtered bookings ────────────────────────────────────────────────────────
  const filtered = useMemo(() => enrichedBookings.filter((b) => {
    if (filterBranch) {
      if (b.branchId && b.branchId !== 'all') {
        if (b.branchId !== filterBranch) return false;
      } else {
        const clinic = b.clinicId ? clinics.find((c) => c.id === b.clinicId) : null;
        const clinicBranchIds = clinic?.branchIds || (clinic?.branchId ? [clinic.branchId] : []);
        if (!clinicBranchIds.includes(filterBranch)) return false;
      }
    }
    if (filterClinic  && b.clinicId       !== filterClinic)  return false;
    if (filterProf    && b.professionalId !== filterProf)    return false;
    if (filterSpec    && b.specialtyId    !== filterSpec)    return false;
    // B1: Filtrar por capacityId (con fallback a scheduleId para citas legacy)
    if (filterCapacity) {
      if (b.capacityId && b.capacityId !== filterCapacity) return false;
      if (!b.capacityId && b.scheduleId) {
        const sid = b.scheduleId.split('_');
        const schedCapId = sid[3] || '';
        if (schedCapId !== filterCapacity) return false;
      }
    }
    if (filterStatus  && b.status        !== filterStatus)   return false;
    // Ocultar citas canceladas por defecto (solo visibles si se filtra explícitamente)
    if (!filterStatus && b.status === 'cancelled') return false;
    return true;
  }), [enrichedBookings, filterBranch, filterClinic, filterProf, filterSpec, filterCapacity, filterStatus, clinics]);

  const todayStr = toDateStr(new Date());

  // Tick every minute for current-time line
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Scroll to current time when viewing today
  useEffect(() => {
    if (view === 'day' && dayViewRef.current && toDateStr(selectedDate) === todayStr) {
      const currentHour = new Date().getHours();
      const scrollPosition = Math.max(0, (currentHour - WORK_HOURS[0]) * HOUR_HEIGHT); // HOUR_HEIGHTpx height per hour row
      dayViewRef.current.scrollTop = scrollPosition;
    }
  }, [view, selectedDate, todayStr]);

  // Day bookings for day view
  const dayBookings = useMemo(() => {
    const dayFiltered = filtered.filter((b) => b.date === toDateStr(selectedDate));
    // C6: Buscador de citas en vista día
    if (!daySearch.trim()) return dayFiltered;
    const q = daySearch.trim().toLowerCase();
    return dayFiltered.filter((b) => {
      const haystack = `${b.ownerName || ''} ${b.petName || ''} ${b.ownerRut || ''} ${b.ownerCelular || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [filtered, selectedDate, daySearch]);

  // Week bookings for week view
  const weekStart = useMemo(() => startOfWeek(selectedDate), [selectedDate]);
  const weekDays  = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekBookings = useMemo(() => {
    const ws = toDateStr(weekDays[0]), we = toDateStr(weekDays[6]);
    return filtered.filter((b) => b.date >= ws && b.date <= we);
  }, [filtered, weekDays]);

  // Stats — always kept for today (used by stat cards when label='hoy')
  const todayBookings = useMemo(() => enrichedBookings.filter((b) => b.date === todayStr), [enrichedBookings, todayStr]);

  // Stats base: view-aware
  const statsBookings = useMemo(() => {
    if (view === 'week') {
      const ws = toDateStr(weekDays[0]), we = toDateStr(weekDays[6]);
      return filtered.filter((b) => b.date >= ws && b.date <= we);
    }
    if (view === 'month') {
      const ms = toDateStr(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      const me = toDateStr(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0));
      return filtered.filter((b) => b.date >= ms && b.date <= me);
    }
    if (view === 'list') return filtered.filter((b) => b.date >= todayStr);
    return filtered.filter((b) => b.date === toDateStr(selectedDate));
  }, [view, selectedDate, enrichedBookings, filtered, weekDays, todayStr]);

  const statsLabel = view === 'week'
    ? 'esta semana'
    : view === 'month'
    ? 'este mes'
    : view === 'list'
    ? 'próximas'
    : toDateStr(selectedDate) === todayStr
    ? 'hoy'
    : formatDateShort(selectedDate);

  const stats = useMemo(() => ({
    total:     statsBookings.length,
    confirmed: statsBookings.filter((b) => ['confirmed', 'pending', 'en_sala'].includes(b.status)).length,
    completed: statsBookings.filter((b) => b.status === 'completed').length,
    cancelled: statsBookings.filter((b) => ['cancelled', 'no_show', 'paciente_cancelo'].includes(b.status)).length,
  }), [statsBookings]);

  // Citas próximas en los siguientes 30 minutos (solo en vista día y hoy)
  const upcomingSoon = useMemo(() => {
    if (toDateStr(selectedDate) !== todayStr) return [];
    const now   = new Date();
    const in30  = new Date(now.getTime() + 30 * 60000);
    const pad   = (n) => String(n).padStart(2, '0');
    const nowStr  = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const in30Str = `${pad(in30.getHours())}:${pad(in30.getMinutes())}`;
    return dayBookings.filter(b =>
      ['confirmed', 'pending', 'en_sala'].includes(b.status) &&
      b.time >= nowStr && b.time <= in30Str
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayBookings, selectedDate, todayStr, tick]);

  // Professionals to show in day view columns
  // (all with active schedule for that weekday OR with bookings)
  const dayOfWeekStr = String(selectedDate.getDay());

  const dayProfs = useMemo(() => {
    const withScheduleOrBooking = filteredProfessionals.filter((p) => {
      const hasBooking = dayBookings.some((b) => b.professionalId === p.id);
      const hasSched = Object.keys(profSchedules).some((key) => {
        const [pid, cid, sid, capid] = key.split('_');
        return pid === p.id &&
          (!filterClinic || cid === filterClinic) &&
          (!filterSpec || sid === filterSpec) &&
          (!filterCapacity || capid === filterCapacity) &&
          scheduleMatchesBranch(key, cid) &&
          profSchedules[key]?.[dayOfWeekStr]?.active;
      });
      return hasBooking || hasSched;
    });
    return withScheduleOrBooking;
  }, [filteredProfessionals, filterClinic, filterSpec, filterCapacity, dayBookings, profSchedules, dayOfWeekStr, scheduleMatchesBranch]);

  // ── Schedule helpers ──────────────────────────────────────────────────────────
  const isFullDayBlocked = useCallback((profId, dateStr, clinicId, specialtyId, capacityId) => {
    const key = clinicId && specialtyId && capacityId ? `${profId}_${clinicId}_${specialtyId}_${capacityId}` : null;
    return allBlocks.some((b) =>
      b.professionalId === profId &&
      b.date === dateStr &&
      b.blockType === 'full_day' &&
      (!clinicId || b.clinicId === clinicId) &&
      (!specialtyId || b.specialtyId === specialtyId) &&
      (!capacityId || b.capacityId === capacityId)
    );
  }, [allBlocks]);

  const getPartialBlocks = useCallback((profId, dateStr, clinicId, specialtyId, capacityId) =>
    allBlocks.filter((b) =>
      b.professionalId === profId &&
      b.date === dateStr &&
      b.blockType !== 'full_day' &&
      (!clinicId || b.clinicId === clinicId) &&
      (!specialtyId || b.specialtyId === specialtyId) &&
      (!capacityId || b.capacityId === capacityId)
    ),
  [allBlocks]);

  const isSlotBlocked = (slotTime, partials) =>
    partials.some((b) => b.startTime && b.endTime && slotTime >= b.startTime && slotTime <= b.endTime);

  const getProfSlotsForHour = useCallback((profId, hour, clinicId, specialtyId, capacityId) => {
    const key = clinicId && specialtyId && capacityId ? `${profId}_${clinicId}_${specialtyId}_${capacityId}` : null;
    const sched = key ? profSchedules[key]?.[dayOfWeekStr] : null;
    if (!sched?.active || !sched.slots?.length) return [];
    const prefix = `${String(hour).padStart(2, '0')}:`;
    return sched.slots.filter((s) => s.time.startsWith(prefix));
  }, [profSchedules, dayOfWeekStr]);

  // ── Status update ────────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    setUpdating(true);
    try {
      if (newStatus === 'deleted') {
        // Borrado lógico: marcar como deleted sin eliminar el documento
        await updateDocument('onlineBookings', selected.id, {
          status: 'deleted',
          deletedAt: new Date(),
          updatedAt: new Date(),
        });
        setDetailOpen(false);
        setSelected(null);
        // D2: Notificar automáticamente al primer paciente en lista de espera
        try {
          const waitlistEntry = await notifyNextWaitlistEntry(
            selected.professionalId,
            selected.date,
            selected.time
          );
          if (waitlistEntry) {
            const msg = `Hola ${waitlistEntry.ownerName}, se liberó un cupo para ${waitlistEntry.date} a las ${waitlistEntry.slotTime} con su mascota ${waitlistEntry.petName}. Responde para confirmar.`;
            const phone = (waitlistEntry.ownerCelular || '').replace(/[^0-9]/g, '');
            const waLink = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : '';
            setSnackbar({
              open: true,
              message: `Cupo liberado. ${waitlistEntry.ownerName} (${waitlistEntry.petName}) notificado en lista de espera`,
              severity: 'info',
            });
            if (waLink) window.open(waLink, '_blank');
          } else {
            setSnackbar({ open: true, message: 'Cita eliminada. No hay pacientes en lista de espera para este horario.', severity: 'success' });
          }
        } catch {
          setSnackbar({ open: true, message: 'Cita eliminada', severity: 'success' });
        }
      } else {
        await updateDocument('onlineBookings', selected.id, { status: newStatus });
        setSelected((prev) => ({ ...prev, status: newStatus }));
        setSnackbar({ open: true, message: 'Estado actualizado', severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Error al actualizar', severity: 'error' });
    } finally {
      setUpdating(false);
      setStatusMenuAnchor(null);
    }
  };

  // ── Open detail ──────────────────────────────────────────────────────────────
  const openDetail = (booking) => { setSelected(booking); setDetailOpen(true); setDetailNotas(booking.notas || ''); };

  const openEditPatient = useCallback(() => {
    if (!selected) return;
    const patient = patientsMap[selected.petId || selected.patientId];
    const owner = ownersMap[selected.ownerId] || ownersMap[patient?.ownerId] || {};
    setEditPatientInitial({
      patientId: selected.petId,
      ownerId: selected.ownerId || patient?.ownerId || owner.id,
      name: selected.petName,
      species: selected.petSpecies,
      breed: selected.petBreed,
      genero: selected.petGender,
      birthDate: patient?.birthDate || '',
      weight: patient?.weight != null ? String(patient.weight) : '',
      branchIds: patient?.branchIds || [],
      ownerName: selected.ownerName || owner.name,
      ownerRut: selected.ownerRut || owner.rut,
      ownerCelular: selected.ownerCelular || owner.celular,
      ownerMail: selected.ownerMail || owner.mail,
      ownerRegion: owner.region,
      ownerComuna: owner.comuna,
      ownerConvenio: owner.convenio,
    });
    setEditPatientOpen(true);
  }, [selected, patientsMap, ownersMap]);

  // ── Reagendar ─────────────────────────────────────────────────────────────────
  const openReagendar = (booking) => {
    setSelected(booking);
    setReagendarDate(booking.date);
    setReagendarTime('');
    setReagendarOpen(true);
  };

  const loadReagendarSlots = useCallback(async (date) => {
    if (!selected || !date) return;
    setReagendarLoading(true);
    try {
      const dateObj = new Date(`${date}T12:00:00`);
      const slots = await getAvailableSlots(
        dateObj,
        selected.professionalId,
        selected.branchId || '',
        selected.specialtyId || null
      );
      setReagendarSlots(slots.filter((s) => s.available));
    } catch {
      setReagendarSlots([]);
    } finally {
      setReagendarLoading(false);
    }
  }, [selected]);

  useEffect(() => {
    if (reagendarOpen) loadReagendarSlots(reagendarDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reagendarOpen, reagendarDate]);

  const handleReagendarSave = async () => {
    if (!selected || !reagendarDate || !reagendarTime) return;
    try {
      await updateDocument('onlineBookings', selected.id, {
        date: reagendarDate,
        time: reagendarTime,
        timeLabel: reagendarTime,
        updatedAt: new Date(),
      });
      setSnackbar({ open: true, message: `Cita reagendada para ${reagendarDate} · ${reagendarTime}`, severity: 'success' });
      setReagendarOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Error al reagendar la cita', severity: 'error' });
    }
  };

  // ── Reasignar médico ─────────────────────────────────────────────────────────
  const openReassign = (booking) => {
    setSelected(booking);
    setReassignProfId(booking.professionalId || '');
    setReassignOpen(true);
  };

  const handleReassignSave = async () => {
    if (!selected || !reassignProfId || reassignProfId === selected.professionalId) {
      setSnackbar({ open: true, message: 'Selecciona un médico distinto al actual', severity: 'warning' });
      return;
    }
    const newProf = professionals.find((p) => p.id === reassignProfId);
    if (!newProf) { setSnackbar({ open: true, message: 'Médico no encontrado', severity: 'error' }); return; }
    // A5: Validar que el profesional destino tenga agenda activa para esa fecha y hora
    const dayOfWeek = String(new Date(`${selected.date}T12:00:00`).getDay());
    const hasSchedule = Object.keys(profSchedules).some((key) => {
      const [pid] = key.split('_');
      return pid === reassignProfId && profSchedules[key]?.[dayOfWeek]?.active &&
        (profSchedules[key]?.[dayOfWeek]?.slots || []).some((s) => s.time === selected.time);
    });
    if (!hasSchedule) {
      setSnackbar({ open: true, message: 'El médico destino no tiene agenda configurada para ese día y hora', severity: 'warning' });
      return;
    }
    // Validar que haya cupo disponible
    const activeAtTarget = dayBookings.filter((bk) =>
      bk.professionalId === reassignProfId &&
      bk.time === selected.time &&
      ['confirmed', 'pending', 'en_sala'].includes(bk.status)
    ).length;
    const targetKey = Object.keys(profSchedules).find((key) => {
      const [pid] = key.split('_');
      return pid === reassignProfId && profSchedules[key]?.[dayOfWeek]?.active &&
        (profSchedules[key]?.[dayOfWeek]?.slots || []).some((s) => s.time === selected.time);
    });
    const targetSlot = targetKey ? profSchedules[targetKey]?.[dayOfWeek]?.slots?.find((s) => s.time === selected.time) : null;
    const targetCapacity = targetSlot?.capacity || 1;
    if (activeAtTarget >= targetCapacity) {
      setSnackbar({ open: true, message: 'El médico destino no tiene cupos disponibles en ese horario', severity: 'warning' });
      return;
    }
    setReassignSaving(true);
    try {
      await updateDocument('onlineBookings', selected.id, {
        professionalId: reassignProfId,
        professionalName: `Dr/a. ${newProf.nombre || ''} ${newProf.apellidos || ''}`.trim(),
        updatedAt: new Date(),
      });
      setSnackbar({ open: true, message: `Cita reasignada a ${newProf.nombre || ''} ${newProf.apellidos || ''}`, severity: 'success' });
      setReassignOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Error al reasignar médico', severity: 'error' });
    } finally {
      setReassignSaving(false);
    }
  };

  // ── Move booking via drag & drop ─────────────────────────────────────────────
  const handleDropBooking = async (profId, dateStr, targetTime) => {
    if (!dragBooking) return;
    const b = dragBooking;
    setDragBooking(null);
    setDragOverKey(null);
    if (b.professionalId === profId && b.date === dateStr && b.time === targetTime) return;
    if (!['confirmed', 'pending', 'en_sala'].includes(b.status)) {
      setSnackbar({ open: true, message: 'Solo se pueden mover citas activas', severity: 'warning' });
      return;
    }
    const prof = professionals.find((p) => p.id === profId);
    const [h] = targetTime.split(':');
    const slots = getProfSlotsForHour(profId, Number(h), filterClinic, filterSpec, filterCapacity);
    const targetSlot = slots.find((s) => s.time === targetTime);
    if (!targetSlot) {
      setSnackbar({ open: true, message: 'El horario no está configurado para este profesional', severity: 'warning' });
      return;
    }
    const activeAtTarget = dayBookings.filter((bk) =>
      bk.professionalId === profId &&
      bk.time === targetTime &&
      ['confirmed', 'pending', 'en_sala'].includes(bk.status)
    ).length;
    if (activeAtTarget >= (targetSlot.capacity || 1)) {
      setSnackbar({ open: true, message: 'El horario seleccionado no tiene cupos disponibles', severity: 'warning' });
      return;
    }
    try {
      const update = { date: dateStr, time: targetTime, timeLabel: targetTime, updatedAt: new Date() };
      if (profId !== b.professionalId) {
        update.professionalId = profId;
        update.professionalName = prof ? `${prof.nombre} ${prof.apellidos}` : b.professionalName;
      }
      await updateDocument('onlineBookings', b.id, update);
      setSnackbar({ open: true, message: `Cita movida a las ${targetTime}`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Error al mover la cita', severity: 'error' });
    }
  };

  // ── Navigation ───────────────────────────────────────────────────────────────
  const navPrev = () => setSelectedDate((d) =>
    view === 'month' ? addMonths(d, -1) : addDays(d, view === 'week' ? -7 : -1)
  );
  const navNext = () => setSelectedDate((d) =>
    view === 'month' ? addMonths(d, 1) : addDays(d, view === 'week' ? 7 : 1)
  );
  const navToday = () => setSelectedDate(new Date());

  // ── Quick action handlers ───────────────────────────────────────────────────
  const handleQuickStatus = useCallback(async (booking, newStatus) => {
    try {
      await updateDocument('onlineBookings', booking.id, { status: newStatus, updatedAt: new Date() });
      setSnackbar({
        open: true,
        message: `${booking.petName}: ${STATUS_CONFIG[newStatus]?.label || newStatus}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Error al actualizar estado', severity: 'error' });
    }
  }, []);

  const handleBookingPay = async ({ paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes }) => {
    if (!payingBooking) return;
    try {
      await registrarVentaOrden({
        presupuestoId: payingBooking.presupuestoId || null,
        orderId: payingBooking.id,
        orderCollection: 'onlineBookings',
        orderType: 'onlineBooking',
        orderNumber: payingBooking.bookingCode || '',
        patientId: payingBooking.petId || '',
        patientName: payingBooking.petName || '',
        patientSpecies: payingBooking.petSpecies || '',
        ownerId: payingBooking.ownerId || '',
        ownerName: payingBooking.ownerName || '',
        ownerRut: payingBooking.ownerRut || '',
        fallbackItems: [{
          nombre: payingBooking.specialtyName || 'Consulta', cantidad: 1,
          precioUnitario: payingBooking.amount || 0,
          tipoPrestacion: 'Consulta', unidadNegocio: 'Consulta',
        }],
        pago: { paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes },
        branchId: payingBooking.branchId || activeBranchId || userData?.branchId || '',
        user: { uid: currentUser?.uid, name: userData?.name, email: currentUser?.email },
        orderExtraUpdate: { arrivedAt: serverTimestamp() },
      });
      setSnackbar({ open: true, message: 'Pago registrado y paciente en sala de espera', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Error al registrar el pago', severity: 'error' });
      throw err;
    } finally {
      setPaymentOpen(false);
      setPayingBooking(null);
    }
  };

  const handleQuickArrive = useCallback(async (booking) => {
    if (booking.source === 'agenda' && booking.paymentStatus !== 'pagado' && !booking.arrivedAt) {
      setPayingBooking(booking);
      setPaymentOpen(true);
      return;
    }
    try {
      await updateDocument('onlineBookings', booking.id, { arrivedAt: new Date(), status: 'en_sala' });
      setSnackbar({ open: true, message: `${booking.petName} en sala de espera`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Error', severity: 'error' });
    }
  }, []);

  // Load patient weight when opening the detail dialog
  useEffect(() => {
    if (!detailOpen || !selected?.petId) {
      setDetailWeight('');
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'patients', selected.petId));
        if (!cancelled) {
          setDetailWeight(snap.exists() ? (snap.data().weight?.toString() || '') : '');
        }
      } catch {
        if (!cancelled) setDetailWeight('');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [detailOpen, selected?.petId]);

  // ── Booking detail tooltip (hover) ────────────────────────────────────────────
  const BookingTooltip = ({ booking }) => {
    const color = profColorMap[booking.professionalId] || PROF_COLORS[0];
    const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.confirmed;
    const branch = branches.find((b) => b.id === booking.branchId);
    const patientForBooking = patientsMap[booking.patientId || booking.petId];
    const ownerFromPatient = patientForBooking ? ownersMap[patientForBooking.ownerId] : null;
    const convenio =
      (ownersMap[booking.ownerId]?.convenio) ||
      (ownerFromPatient?.convenio) ||
      booking.ownerConvenio ||
      '';
    const apptType = APPT_TYPE_CONFIG[booking.appointmentType];
    return (
      <Box sx={{ p: 1, maxWidth: 300 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: color.text }}>
          {booking.time} · {booking.petName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          <Chip label={status.label} size="small" color={status.color} sx={{ height: 18, fontSize: '0.6rem' }} />
          {convenio && <Chip label={convenio} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          {apptType && <Chip label={apptType.label} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', color: apptType.color, borderColor: apptType.color }} />}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Typography sx={{ fontSize: '0.75rem' }} noWrap><PersonIcon sx={{ fontSize: 12, mr: 0.3 }} />{booking.ownerName}</Typography>
        <Typography sx={{ fontSize: '0.75rem' }} noWrap><PetsIcon sx={{ fontSize: 12, mr: 0.3 }} />{booking.petSpecies}{booking.petBreed ? ` · ${booking.petBreed}` : ''}</Typography>
        {booking.specialtyName && <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mt: 0.5 }}>{booking.specialtyName}</Typography>}
        {booking.motivoConsulta && <Typography sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary' }}>"{booking.motivoConsulta}"</Typography>}
        {booking.internalNotes && <Typography sx={{ fontSize: '0.7rem', color: '#B45309', mt: 0.3 }}>{booking.internalNotes}</Typography>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{branch?.name || booking.branchName}</Typography>
          {(() => {
            const pay = getBookingPaymentInfo(booking);
            if (!pay.hasPayment) return null;
            return (
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                {pay.isWebpay
                  ? `Pago Webpay: ${formatPrice(pay.amount)}${pay.auth ? ` · Aut: ${pay.auth}` : ''} · ${pay.label}`
                  : pay.isSinCosto
                    ? `Cita sin costo${pay.convenioName ? ` · Convenio: ${pay.convenioName}` : ''}`
                    : pay.prestacion
                      ? `${pay.prestacion}: ${formatPrice(pay.amount)} · ${pay.label}`
                      : `A pagar: ${formatPrice(pay.amount)} · ${pay.label}`}
              </Typography>
            );
          })()}
        </Box>
      </Box>
    );
  };

  // ── Booking card ─────────────────────────────────────────────────────────────
  const BookingCard = ({ booking, compact = false, height, onQuickStatus, onQuickArrive }) => {
    const color  = profColorMap[booking.professionalId] || PROF_COLORS[0];
    const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.confirmed;
    const branch = branches.find((b) => b.id === booking.branchId);
    const patientForBooking = patientsMap[booking.patientId || booking.petId];
    const ownerFromPatient = patientForBooking ? ownersMap[patientForBooking.ownerId] : null;
    const convenio =
      (ownersMap[booking.ownerId]?.convenio) ||
      (ownerFromPatient?.convenio) ||
      booking.ownerConvenio ||
      '';
    const isDragging = dragBooking?.id === booking.id;
    const isUrgency = booking.appointmentType === 'urgencia';
    const apptType = APPT_TYPE_CONFIG[booking.appointmentType];
    const canDrag = ['confirmed', 'pending', 'en_sala'].includes(booking.status);
    return (
      <Paper
        elevation={isDragging ? 6 : 0}
        draggable={canDrag}
        onDragStart={(e) => {
          if (!canDrag) { e.preventDefault(); return; }
          e.stopPropagation();
          setDragBooking(booking);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => { setDragBooking(null); setDragOverKey(null); }}
        onClick={() => { if (!dragBooking) openDetail(booking); }}
        sx={{
          border: isUrgency ? '2px solid #DC2626' : `1.5px solid ${color.bg}`,
          borderLeft: compact ? `3px solid ${status.border || color.bg}` : `4px solid ${status.border || color.bg}`,
          borderRadius: compact ? 1 : 2,
          p: compact ? 0.5 : 1.25,
          cursor: isDragging ? 'grabbing' : 'grab',
          bgcolor: color.light,
          opacity: isDragging ? 0.45 : 1,
          height: height ? `${height}px` : 'auto',
          overflow: 'hidden',
          '&:hover': { boxShadow: 3, transform: isDragging ? 'none' : 'translateY(-1px)' },
          transition: 'all 0.15s',
          mb: height ? 0 : (compact ? 0.5 : 0),
          ...(isUrgency && {
            '@keyframes urgencyPulse': {
              '0%, 100%': { borderColor: '#DC2626', boxShadow: '0 0 0 0 rgba(220, 38, 38, 0.2)' },
              '50%': { borderColor: '#EF4444', boxShadow: '0 0 0 4px rgba(220, 38, 38, 0)' },
            },
            animation: 'urgencyPulse 2s ease-in-out infinite',
          }),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.3 : 0.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: compact ? '0.7rem' : '0.875rem', fontWeight: 700, color: color.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {booking.time} · {booking.petName}
              </Typography>
              <Chip label={status.label} size="small" color={status.color}
                sx={{ fontSize: compact ? '0.55rem' : '0.65rem', height: compact ? 18 : 20, fontWeight: 600, flexShrink: 0 }} />
              {booking.sobrecupo && (
                <Chip label="Sobrecupo" size="small" color="error" variant="filled"
                  sx={{ fontSize: compact ? '0.5rem' : '0.55rem', height: compact ? 16 : 18, fontWeight: 700, flexShrink: 0 }} />
              )}
              {booking.confirmationStatus === 'confirmed_by_patient' && (
                <Chip label="Confirmado por paciente" size="small" color="success" variant="outlined"
                  sx={{ fontSize: compact ? '0.5rem' : '0.55rem', height: compact ? 16 : 18, fontWeight: 600, flexShrink: 0 }} />
              )}
              {convenio && (
                <Chip label={convenio} size="small" color="info" variant="outlined"
                  sx={{ fontSize: compact ? '0.5rem' : '0.55rem', height: compact ? 16 : 18, fontWeight: 600, flexShrink: 0 }} />
              )}
              {apptType && (
                <Chip label={apptType.label} size="small" variant="outlined"
                  sx={{
                    fontSize: compact ? '0.5rem' : '0.6rem',
                    height: compact ? 16 : 18,
                    fontWeight: 600,
                    flexShrink: 0,
                    bgcolor: apptType.bg,
                    color: apptType.color,
                    borderColor: apptType.color,
                  }} />
              )}
            </Box>
            {(!compact || (height && height > 40)) && (
              <>
                <Typography sx={{ fontSize: compact ? '0.6rem' : '0.75rem', color: 'text.secondary', mt: 0.2 }} display="block" noWrap>
                  <PersonIcon sx={{ fontSize: compact ? 10 : 11, mr: 0.3 }} />{booking.ownerName}
                </Typography>
                <Typography sx={{ fontSize: compact ? '0.6rem' : '0.75rem', color: 'text.secondary' }} noWrap>
                  <PetsIcon sx={{ fontSize: compact ? 10 : 11, mr: 0.3 }} />{booking.petSpecies}{booking.petBreed ? ` · ${booking.petBreed}` : ''}
                </Typography>
                <Typography sx={{ fontSize: compact ? '0.6rem' : '0.75rem', color: 'text.secondary' }} noWrap>
                  <CakeIcon sx={{ fontSize: compact ? 10 : 11, mr: 0.3 }} />{patientForBooking?.birthDate ? calcAge(patientForBooking.birthDate) : '—'}<WcIcon sx={{ fontSize: compact ? 10 : 11, ml: 0.8, mr: 0.3 }} />{patientForBooking?.genero || '—'}
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, alignItems: 'flex-end', flexShrink: 0 }}>
            {booking.arrivedAt && (
              <Tooltip title="En sala de espera">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981',
                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                  animation: 'pulse 1.5s ease-in-out infinite' }}/>
              </Tooltip>
            )}
            {booking.reminderSentAt && (
              <Tooltip title={`Recordatorio enviado${booking.reminderSentBy ? ` por ${booking.reminderSentBy}` : ''}`}>
                <WhatsAppIcon sx={{ fontSize: 14, color: '#22C55E', cursor: 'help' }}/>
              </Tooltip>
            )}
            {!compact && booking.source && (
              <Chip label={booking.source === 'online' ? 'Web' : booking.source === 'agenda' ? 'Agenda' : 'Manual'}
                size="small" variant="outlined" color={booking.source === 'online' ? 'primary' : booking.source === 'agenda' ? 'success' : 'default'}
                sx={{ fontSize: '0.55rem', height: 16 }} />
            )}
          </Box>
        </Box>
        {!compact && (
          <>
            {booking.specialtyName && (
              <Typography variant="caption" sx={{ color: color.text, fontWeight: 600, display: 'block', mt: 0.5 }}>
                {booking.specialtyName}
              </Typography>
            )}
            {booking.motivoConsulta && (
              <Typography variant="caption" sx={{
                display: 'block', color: 'text.secondary', fontSize: '0.62rem', mt: 0.3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic',
              }}>
                "{booking.motivoConsulta}"
              </Typography>
            )}
            {booking.internalNotes && (
              <Box sx={{
                display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5,
                px: 0.8, py: 0.3, bgcolor: '#FFFBEB', borderRadius: 1,
                border: '0.5px solid #FDE68A',
              }}>
                <WarningAmberIcon sx={{ fontSize: 11, color: '#D97706', mt: 0.2, flexShrink: 0 }}/>
                <Typography variant="caption"
                  sx={{ fontSize: '0.62rem', color: '#92400E', lineHeight: 1.3 }}>
                  {booking.internalNotes}
                </Typography>
              </Box>
            )}
            {booking.arrivedAt && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 0.8, py: 0.2, bgcolor: '#D1FAE5', borderRadius: 1, mt: 0.5,
              }}>
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981', flexShrink: 0,
                  '@keyframes dotPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                  animation: 'dotPulse 1.5s ease-in-out infinite',
                }}/>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#065F46', fontWeight: 600 }}>
                  En sala de espera
                </Typography>
              </Box>
            )}
            {booking.estimatedDurationMin && (
              <Typography variant="caption"
                sx={{ display: 'block', color: 'text.disabled', fontSize: '0.6rem', mt: 0.3 }}>
                <AccessTimeIcon sx={{ fontSize: 10, mr: 0.3, verticalAlign: 'middle' }}/>
                {booking.estimatedDurationMin} min estimados
              </Typography>
            )}
            {branch && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                {branch.name}
              </Typography>
            )}
            {(() => {
              const pay = getBookingPaymentInfo(booking);
              if (!pay.hasPayment) return null;
              return (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                  {!pay.isWebpay && pay.prestacion && (
                    <Chip
                      label={pay.prestacion}
                      size="small"
                      sx={{ fontSize: '0.55rem', height: 18, fontWeight: 600, maxWidth: 120 }}
                    />
                  )}
                  {pay.isSinCosto && (
                    <Chip
                      label={pay.convenioName}
                      size="small"
                      color="info"
                      sx={{ fontSize: '0.55rem', height: 18, fontWeight: 600, maxWidth: 120 }}
                    />
                  )}
                  {!pay.isSinCosto && (
                    <Chip
                      label={formatPrice(pay.amount)}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 18, fontWeight: 600 }}
                    />
                  )}
                  {!pay.isSinCosto && (
                    <Chip
                      label={pay.label}
                      size="small"
                      color={pay.paid ? 'success' : 'warning'}
                      sx={{ fontSize: '0.6rem', height: 18, fontWeight: 600 }}
                    />
                  )}
                </Box>
              );
            })()}
            {['confirmed', 'pending', 'en_sala'].includes(booking.status) && onQuickStatus && onQuickArrive && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, pt: 0.5,
                borderTop: '0.5px solid', borderColor: `${color.bg}80` }}
                onClick={e => e.stopPropagation()}>
                {!booking.arrivedAt && (
                  <Button size="small" variant="text"
                    sx={{ fontSize: '0.6rem', py: 0.2, px: 0.8, color: '#10B981', minWidth: 0,
                          bgcolor: '#F0FDF4', borderRadius: 1, '&:hover': { bgcolor: '#DCFCE7' } }}
                    onClick={() => onQuickArrive(booking)}>
                    <PersonIcon sx={{ fontSize: 11, mr: 0.3 }}/> Llegó
                  </Button>
                )}
                <Button size="small" variant="text"
                  sx={{ fontSize: '0.6rem', py: 0.2, px: 0.8, color: '#10B981', minWidth: 0,
                        bgcolor: '#F0FDF4', borderRadius: 1, '&:hover': { bgcolor: '#DCFCE7' } }}
                  onClick={() => onQuickStatus(booking, 'completed')}>
                  ✓ Atendido
                </Button>
                <Button size="small" variant="text"
                  sx={{ fontSize: '0.6rem', py: 0.2, px: 0.8, color: '#DC2626', minWidth: 0,
                        bgcolor: '#FEF2F2', borderRadius: 1, '&:hover': { bgcolor: '#FEE2E2' },
                        ml: 'auto' }}
                  onClick={() => onQuickStatus(booking, 'no_show')}>
                  No vino
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    );
  };

  // ── VIEW: DAY ────────────────────────────────────────────────────────────────
  const renderDayView = () => {
    if (dayProfs.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CalendarMonthIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
          <Typography color="text.secondary">No hay agenda configurada para este día.</Typography>
          <Typography variant="caption" color="text.secondary">
            {filterProf || filterSpec ? 'Intenta quitar los filtros.' : 'Configura horarios en Configuración → Agenda Profesional.'}
          </Typography>
        </Box>
      );
    }

    const cols = dayProfs.length;
    const selectedDateStr = toDateStr(selectedDate);

    return (
      <Box ref={dayViewRef} sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <Box sx={{ minWidth: Math.max(640, cols * 180 + 56) }}>

          {/* ── Column headers ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: `56px repeat(${cols}, minmax(0, 1fr))`, position: 'sticky', top: 0, zIndex: 10, bgcolor: 'white', borderBottom: '2px solid', borderColor: 'divider' }}>
            <Box />
            {dayProfs.map((p) => {
              const color        = profColorMap[p.id] || PROF_COLORS[0];
              const fullBlocked  = isFullDayBlocked(p.id, selectedDateStr, filterClinic, filterSpec, filterCapacity);
              const blockInfo    = allBlocks.find((b) => b.professionalId === p.id && b.date === selectedDateStr && b.blockType === 'full_day' && (!filterClinic || b.clinicId === filterClinic) && (!filterSpec || b.specialtyId === filterSpec) && (!filterCapacity || b.capacityId === filterCapacity));
              // Find active schedule keys for this professional
              const activeMatchingKeys = Object.keys(profSchedules).filter((key) => {
                const [pid, cid, sid, capid] = key.split('_');
                return pid === p.id &&
                  (!filterClinic || cid === filterClinic) &&
                  (!filterSpec || sid === filterSpec) &&
                  (!filterCapacity || capid === filterCapacity) &&
                  scheduleMatchesBranch(key, cid) &&
                  profSchedules[key]?.[dayOfWeekStr]?.active;
              });
              const scheduleKey  = activeMatchingKeys[0] || null;
              const hasSched     = activeMatchingKeys.length > 0;
              const totalSlots   = activeMatchingKeys.reduce((sum, key) =>
                sum + (profSchedules[key]?.[dayOfWeekStr]?.slots?.length || 0), 0);
              const bookedCount  = dayBookings.filter((b) => b.professionalId === p.id && ['confirmed','pending','en_sala'].includes(b.status) && (!filterClinic || b.clinicId === filterClinic) && (!filterSpec || b.specialtyId === filterSpec) && (!filterCapacity || b.capacityId === filterCapacity)).length;
              return (
                <Box key={p.id} sx={{ borderLeft: '1px solid', borderColor: 'divider', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: fullBlocked ? '#9CA3AF' : color.bg, width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700 }}>
                      {fullBlocked ? <LockIcon sx={{ fontSize: 14 }} /> : <>{(p.nombre || '?')[0]}{(p.apellidos || '')[0]}</>}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }} noWrap>
                        Dr/a. {p.nombre} {p.apellidos}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }} noWrap>
                        {(() => {
                          const ids = p.clinicIds || (p.clinicId ? [p.clinicId] : []);
                          const clinicName = ids.length > 0
                            ? ids.map((id) => clinics.find((c) => c.id === id)?.name).filter(Boolean).join(', ')
                            : (p.clinicName || p.especialidad || p.specialtyName || '');
                          const capacityLabel = (() => {
                            if (!scheduleKey) return '';
                            const [pid, cid, sid, capid] = scheduleKey.split('_');
                            const capacity = capacities.find((c) => c.id === capid);
                            return capacity ? `C.${capacity.number}` : '';
                          })();
                          return [clinicName, capacityLabel].filter(Boolean).join(' · ');
                        })()}
                      </Typography>
                    </Box>
                    {hasSched && !fullBlocked && (
                      <Chip
                        label={`${Math.max(0, totalSlots - bookedCount)}/${totalSlots}`}
                        size="small"
                        color={bookedCount >= totalSlots ? 'error' : bookedCount >= totalSlots * 0.8 ? 'warning' : 'success'}
                        sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                  {/* Full-day block banner */}
                  {fullBlocked && (
                    <Box sx={{ mx: 1, mb: 0.5, px: 0.75, py: 0.25, bgcolor: '#FEE2E2', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LockIcon sx={{ fontSize: 11, color: '#EF4444' }} />
                      <Typography sx={{ fontSize: '0.65rem', color: '#B91C1C', fontWeight: 600 }} noWrap>
                        {blockInfo?.reason || 'Agenda bloqueada'}
                      </Typography>
                    </Box>
                  )}
                  {!hasSched && !fullBlocked && (
                    <Box sx={{ mx: 1, mb: 0.5, px: 0.75, py: 0.25, bgcolor: '#F3F4F6', borderRadius: 1 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>Sin horarios configurados</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* ── Time rows ── */}
          {WORK_HOURS.map((hour) => {
            const timeStr = `${String(hour).padStart(2, '0')}:`;
            // Current time indicator
            const nowRef = new Date(tick >= 0 ? Date.now() : 0);
            const isCurrentHour = toDateStr(selectedDate) === todayStr && hour === nowRef.getHours();
            const timeLineTop = isCurrentHour ? `${(nowRef.getMinutes() / 60) * HOUR_HEIGHT}px` : null;
            return (
              <Box key={hour} sx={{ display: 'grid', gridTemplateColumns: `56px repeat(${cols}, minmax(0, 1fr))`, borderBottom: '1px solid', borderColor: 'grey.100', minHeight: HOUR_HEIGHT, position: 'relative' }}>
                {/* Current time line */}
                {timeLineTop && (
                  <Box sx={{ position: 'absolute', top: timeLineTop, left: 0, right: 0, zIndex: 20, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                    <Box sx={{ width: 70, display: 'flex', justifyContent: 'flex-end', pr: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#EF4444', fontWeight: 700 }}>
                        {`${String(nowRef.getHours()).padStart(2,'0')}:${String(nowRef.getMinutes()).padStart(2,'0')}`}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#EF4444', flexShrink: 0, ml: '-4px' }} />
                    <Box sx={{ flex: 1, height: 2, bgcolor: '#EF4444', opacity: 0.7 }} />
                  </Box>
                )}
                {/* Hour label */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', pr: 1, pt: 0.5, bgcolor: '#FAFAFA' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: isCurrentHour ? '#EF4444' : 'text.secondary' }}>{String(hour).padStart(2, '0')}:00</Typography>
                </Box>

                {/* Professional columns */}
                {dayProfs.map((p) => {
                  const fullBlocked    = isFullDayBlocked(p.id, selectedDateStr, filterClinic, filterSpec, filterCapacity);
                  const partials       = getPartialBlocks(p.id, selectedDateStr, filterClinic, filterSpec, filterCapacity);
                  // Find matching schedule data for this hour across all active schedules
                  const slotMap = new Map();
                  Object.keys(profSchedules).forEach((key) => {
                    const [pid, cid, sid, capid] = key.split('_');
                    if (pid === p.id &&
                      (!filterClinic || cid === filterClinic) &&
                      (!filterSpec || sid === filterSpec) &&
                      (!filterCapacity || capid === filterCapacity) &&
                      scheduleMatchesBranch(key, cid) &&
                      profSchedules[key]?.[dayOfWeekStr]?.active) {
                      const slots = profSchedules[key]?.[dayOfWeekStr]?.slots || [];
                      const prefix = `${String(hour).padStart(2, '0')}:`;
                      slots.filter((s) => s.time.startsWith(prefix)).forEach((s) => {
                        const existing = slotMap.get(s.time);
                        if (existing) {
                          existing.capacity = (existing.capacity || 1) + (s.capacity || 1);
                        } else {
                          slotMap.set(s.time, { ...s, capacity: s.capacity || 1 });
                        }
                      });
                    }
                  });
                  const hourSlots = Array.from(slotMap.values()).sort((a, b) => a.time.localeCompare(b.time));
                  const cellBookings   = dayBookings.filter((b) =>
                    b.professionalId === p.id && b.time?.startsWith(timeStr) &&
                    (!filterClinic || b.clinicId === filterClinic) &&
                    (!filterSpec || b.specialtyId === filterSpec) &&
                    (!filterCapacity || b.capacityId === filterCapacity)
                  );

                  // Striped background for full-day blocked
                  const cellBg = fullBlocked
                    ? 'repeating-linear-gradient(45deg,#FEF2F2,#FEF2F2 6px,#fff 6px,#fff 12px)'
                    : hourSlots.length ? 'transparent' : '#FAFAFA';
                  const cellDropKey = `${p.id}_${hour}`;
                  const isCellDropOver = !!dragBooking && !fullBlocked && dragOverKey === cellDropKey;

                  return (
                    <Box
                      key={p.id}
                      onDragOver={(e) => {
                        if (dragBooking && !fullBlocked) {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverKey(cellDropKey);
                        }
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverKey(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragBooking || fullBlocked) return;
                        const targetTime = hourSlots.length > 0
                          ? hourSlots[0].time
                          : `${String(hour).padStart(2, '0')}:00`;
                        handleDropBooking(p.id, selectedDateStr, targetTime);
                      }}
                      sx={{
                        borderLeft: '1px solid',
                        borderColor: isCellDropOver ? '#3B82F6' : 'grey.100',
                        p: 0.5,
                        background: isCellDropOver ? '#EFF6FF' : cellBg,
                        position: 'relative',
                        outline: isCellDropOver ? '2px dashed #3B82F6' : 'none',
                        transition: 'background 0.1s, outline 0.1s',
                        overflow: 'hidden',
                      }}
                    >

                      {/* Slot chips (capacity indicators) */}
                      {!fullBlocked && hourSlots.map((slot) => {
                        const slotBookings = dayBookings.filter((b) =>
                          b.professionalId === p.id && b.time === slot.time &&
                          ['confirmed', 'pending', 'en_sala'].includes(b.status)
                        ).length;
                        const capacity   = slot.capacity || 1;
                        const available  = capacity - slotBookings;
                        const blocked    = isSlotBlocked(slot.time, partials);
                        const hasBooking = cellBookings.some((b) => b.time === slot.time);

                        if (hasBooking) return null; // booking card takes priority

                        if (blocked) {
                          return (
                            <Box key={slot.time} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, mb: 0.4, bgcolor: '#FEE2E2', borderRadius: 1, border: '1px solid #FECACA' }}>
                              <LockIcon sx={{ fontSize: 11, color: '#EF4444' }} />
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#B91C1C', fontWeight: 600 }}>{slot.time} · Bloqueado</Typography>
                            </Box>
                          );
                        }

                        const chipColor = available === 0 ? '#FEF2F2' : available === 1 ? '#FFFBEB' : '#F0FDF4';
                        const textColor = available === 0 ? '#EF4444' : available === 1 ? '#D97706' : '#16A34A';
                        const dotColor  = available === 0 ? '#EF4444' : available === 1 ? '#F59E0B' : '#22C55E';
                        const slotDropKey = `${p.id}_${slot.time}`;
                        const isSlotDropOver = !!dragBooking && dragOverKey === slotDropKey;

                        const waitlistCount = waitlistEntries.filter(w =>
                          w.professionalId === p.id && w.date === selectedDateStr &&
                          w.slotTime === slot.time && w.status === 'waiting'
                        ).length;

                        return (
                          <Tooltip key={slot.time} title={isSlotDropOver ? 'Soltar aquí para mover' : available > 0 ? 'Haz clic para agendar' : 'Citar sobrecupo'} placement="top">
                            <Box
                              onDragOver={(e) => { if (dragBooking) { e.preventDefault(); e.stopPropagation(); setDragOverKey(slotDropKey); }}}
                              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverKey(null); }}
                              onDrop={(e) => { e.preventDefault(); if (dragBooking) handleDropBooking(p.id, selectedDateStr, slot.time); }}
                              onClick={() => !dragBooking && (available > 0 ? handleOpenBookDlg(selectedDate, slot.time, p) : handleOpenBookDlg(selectedDate, slot.time, p, true))}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, mb: 0.2,
                                bgcolor: isSlotDropOver ? '#DBEAFE' : chipColor,
                                borderRadius: 1,
                                border: isSlotDropOver ? '2px solid #3B82F6' : `1px solid ${dotColor}30`,
                                cursor: dragBooking ? 'copy' : 'pointer',
                                transform: isSlotDropOver ? 'scale(1.04)' : 'none',
                                '&:hover': !dragBooking ? { boxShadow: 1, transform: 'scale(1.02)' } : {},
                                transition: 'all 0.1s' }}
                            >
                              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: '0.6rem', color: textColor, fontWeight: 600 }}>
                                {slot.time}
                              </Typography>
                              <Typography sx={{ fontSize: '0.55rem', color: textColor, ml: 'auto' }}>
                                {available === 0 ? 'Completo' : `${available} libre${available > 1 ? 's' : ''}`}
                              </Typography>
                              {available === 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 0.3 }}>
                                  {waitlistCount > 0 && (
                                    <Chip label={`${waitlistCount} espera`} size="small"
                                      sx={{ height: 14, fontSize: '0.52rem', bgcolor: '#FEF3C7', color: '#92400E', px: 0 }}/>
                                  )}
                                  <Tooltip title="Agregar a lista de espera">
                                    <IconButton size="small" sx={{ p: 0.2 }}
                                      onClick={e => { e.stopPropagation(); setWaitlistDlgData({ slot, professional: p }); setWaitlistDlgOpen(true); }}>
                                      <AddCircleOutlineIcon sx={{ fontSize: 13, color: '#D97706' }}/>
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}

                      {/* Booking cards */}
                      {Object.entries(
                        cellBookings
                          .slice()
                          .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                          .reduce((acc, b) => {
                            (acc[b.time] = acc[b.time] || []).push(b);
                            return acc;
                          }, {})
                      ).flatMap(([time, bookings]) =>
                        bookings.map((b, idx) => {
                          const [h, m] = (b.time || '00:00').split(':').map(Number);
                          const minutes = h * 60 + m;
                          const top = ((minutes % 60) / 60) * HOUR_HEIGHT + idx * 18;
                          const duration = Math.max(15, b.estimatedDurationMin || 30);
                          const height = (duration / 60) * HOUR_HEIGHT;
                          return (
                            <Box
                              key={b.id}
                              sx={{
                                position: 'absolute',
                                top: `${top}px`,
                                left: `${4 + idx * 4}px`,
                                right: 4,
                                height: `${height}px`,
                                zIndex: 2 + idx,
                              }}
                            >
                              <Tooltip
                                title={<BookingTooltip booking={b} />}
                                placement="top"
                                arrow
                                enterDelay={300}
                                leaveDelay={100}
                                slotProps={{
                                  tooltip: { sx: { maxWidth: 320, bgcolor: 'background.paper', color: 'text.primary', boxShadow: 4, border: '1px solid', borderColor: 'divider' } },
                                  arrow: { sx: { color: 'background.paper' } },
                                }}
                              >
                                <Box sx={{ height: '100%', width: '100%' }}>
                                  <BookingCard
                                    booking={b}
                                    compact
                                    height={height}
                                    onQuickStatus={handleQuickStatus}
                                    onQuickArrive={handleQuickArrive}
                                  />
                                </Box>
                              </Tooltip>
                            </Box>
                          );
                        })
                      )}

                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ── VIEW: WEEK ───────────────────────────────────────────────────────────────
  const renderWeekView = () => (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 700, display: 'grid', gridTemplateColumns: `repeat(7, 1fr)`, gap: 0 }}>
        {weekDays.map((day) => {
          const ds             = toDateStr(day);
          const dow            = String(day.getDay());
          const isToday        = ds === todayStr;
          const isWeekend      = day.getDay() === 0 || day.getDay() === 6;
          const dayBks         = weekBookings.filter((b) => b.date === ds);

          // Availability summary per day
          const profsWithSched = filteredProfessionals.filter((p) => {
            if (filterClinic && !(p.clinicIds?.includes(filterClinic) || p.clinicId === filterClinic)) return false;
            return Object.keys(profSchedules).some((key) => {
              const [pid, cid, sid, capid] = key.split('_');
              return pid === p.id &&
                (!filterClinic || cid === filterClinic) &&
                (!filterSpec || sid === filterSpec) &&
                (!filterCapacity || capid === filterCapacity) &&
                scheduleMatchesBranch(key, cid) &&
                profSchedules[key]?.[dow]?.active;
            });
          });
          const totalSlots     = profsWithSched.reduce((sum, p) => {
            let pSlots = 0;
            Object.keys(profSchedules).forEach((key) => {
              const [pid, cid, sid, capid] = key.split('_');
              if (pid === p.id &&
                (!filterClinic || cid === filterClinic) &&
                (!filterSpec || sid === filterSpec) &&
                (!filterCapacity || capid === filterCapacity) &&
                scheduleMatchesBranch(key, cid) &&
                profSchedules[key]?.[dow]?.active) {
                pSlots += (profSchedules[key]?.[dow]?.slots?.length || 0);
              }
            });
            return sum + pSlots;
          }, 0);
          const bookedCount    = dayBks.filter((b) => ['confirmed','pending'].includes(b.status) && (!filterClinic || b.clinicId === filterClinic) && (!filterSpec || b.specialtyId === filterSpec) && (!filterCapacity || b.capacityId === filterCapacity)).length;
          const availableTotal = Math.max(0, totalSlots - bookedCount);

          // A4: Upcoming appointment indicator
          const nearBookings = isToday ? dayBks.filter(b => {
            if (!['confirmed','pending'].includes(b.status)) return false;
            if (!b.time) return false;
            const [h, m] = b.time.split(':').map(Number);
            const bookingTime = new Date(); bookingTime.setHours(h, m, 0, 0);
            const diff = (bookingTime - new Date()) / 60000;
            return diff >= 0 && diff <= 30;
          }) : [];

          return (
            <Box key={ds} sx={{ borderLeft: '1px solid', borderColor: 'divider', minHeight: 320 }}>

              {/* ── Day header (A2: click to go to day view, A6: weekend styling) ── */}
              <Box
                onClick={() => { setSelectedDate(day); setView('day'); }}
                sx={{
                  p: 1,
                  bgcolor: isToday ? 'primary.main' : isWeekend ? '#F9FAFB' : '#F8FAFC',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                  borderTop: isWeekend ? '1px solid #E5E7EB' : 'none',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}>
                <Typography variant="caption" sx={{ color: isToday ? 'white' : isWeekend ? 'text.disabled' : 'text.secondary', fontWeight: 600 }}>
                  {DAY_NAMES_SHORT[day.getDay()]}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isToday ? 'white' : isWeekend ? 'text.disabled' : 'text.primary', lineHeight: 1.2, textDecoration: 'underline', textDecorationColor: 'transparent', '&:hover': { textDecorationColor: 'inherit' } }}>
                  {day.getDate()}
                </Typography>
                {totalSlots > 0 && (
                  <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {bookedCount > 0 && (
                      <Chip label={`${bookedCount} cita${bookedCount > 1 ? 's' : ''}`} size="small"
                        sx={{ fontSize: '0.58rem', height: 16, bgcolor: isToday ? 'rgba(255,255,255,0.25)' : '#FEF3C7', color: isToday ? 'white' : '#92400E' }} />
                    )}
                    <Chip
                      label={availableTotal === 0 ? 'Completo' : `${availableTotal} libre${availableTotal !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ fontSize: '0.58rem', height: 16,
                        bgcolor: isToday ? 'rgba(255,255,255,0.25)' : availableTotal === 0 ? '#FEE2E2' : availableTotal <= 3 ? '#FEF3C7' : '#DCFCE7',
                        color: isToday ? 'white' : availableTotal === 0 ? '#B91C1C' : availableTotal <= 3 ? '#92400E' : '#166534' }}
                    />
                  </Box>
                )}
                {/* A4: Upcoming appointment banner */}
                {isToday && nearBookings.length > 0 && (
                  <Box sx={{ mx: 0.5, mb: 0.5, px: 1, py: 0.3,
                    bgcolor: '#FEF3C7', borderRadius: 1, border: '1px solid #F59E0B' }}>
                    <Typography variant="caption" sx={{ color: '#92400E', fontSize: '0.58rem', fontWeight: 700 }}>
                      ⏰ {nearBookings[0].time} · {nearBookings[0].petName}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* ── Per-professional availability rows (A1: click to filter day view) ── */}
              {profsWithSched.length > 0 && (
                <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.100', px: 0.75, py: 0.5, bgcolor: '#FAFAFA' }}>
                  {profsWithSched.map((p) => {
                    const profColor   = profColorMap[p.id] || PROF_COLORS[0];
                    const fullBlocked = isFullDayBlocked(p.id, ds, filterClinic, filterSpec, filterCapacity);
                    const blockReason = allBlocks.find((b) => b.professionalId === p.id && b.date === ds && b.blockType === 'full_day' && (!filterClinic || b.clinicId === filterClinic) && (!filterSpec || b.specialtyId === filterSpec) && (!filterCapacity || b.capacityId === filterCapacity))?.reason;
                    let slots = [];
                    Object.keys(profSchedules).forEach((key) => {
                      const [pid, cid, sid, capid] = key.split('_');
                      if (pid === p.id &&
                        (!filterClinic || cid === filterClinic) &&
                        (!filterSpec || sid === filterSpec) &&
                        (!filterCapacity || capid === filterCapacity) &&
                        scheduleMatchesBranch(key, cid) &&
                        profSchedules[key]?.[dow]?.active) {
                        slots = profSchedules[key]?.[dow]?.slots || [];
                      }
                    });
                    const bkd         = dayBks.filter((b) => b.professionalId === p.id && ['confirmed','pending'].includes(b.status) && (!filterClinic || b.clinicId === filterClinic) && (!filterSpec || b.specialtyId === filterSpec) && (!filterCapacity || b.capacityId === filterCapacity)).length;
                    const avail       = Math.max(0, slots.length - bkd);
                    return (
                      <Box
                        key={p.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterProf(p.id);
                          setView('day');
                          setSelectedDate(day);
                        }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.5, py: 0.3,
                          cursor: 'pointer', borderRadius: 0.5, px: 0.5,
                          '&:hover': {
                            bgcolor: 'white',
                            '& .prof-name-text': { color: '#1A3A5C', fontWeight: 600 },
                          },
                          '& .prof-arrow': { opacity: 0 },
                          '&:hover .prof-arrow': { opacity: 1 },
                        }}
                      >
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: fullBlocked ? '#EF4444' : profColor.bg, flexShrink: 0 }} />
                        <Typography className="prof-name-text" variant="caption"
                          sx={{ flex: 1, fontSize: '0.6rem', color: 'text.secondary',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'color 0.1s' }}>
                          {p.apellidos || p.nombre}
                        </Typography>
                        {fullBlocked ? (
                          <Tooltip title={blockReason || 'Agenda bloqueada'}>
                            <LockIcon sx={{ fontSize: 11, color: '#EF4444', flexShrink: 0 }} />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption"
                            sx={{ fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
                                  color: avail === 0 ? '#EF4444' : avail <= 2 ? '#D97706' : '#16A34A' }}>
                            {avail}/{slots.length}
                          </Typography>
                        )}
                        <ChevronRightIcon className="prof-arrow"
                          sx={{ fontSize: 12, color: '#1A3A5C', flexShrink: 0, transition: 'opacity 0.1s' }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
              {profsWithSched.length === 0 && (
                <Box sx={{ px: 1, py: 0.5, bgcolor: '#FAFAFA', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>Sin horarios configurados</Typography>
                </Box>
              )}

              {/* ── Booking cards (A3: tooltip on hover) ── */}
              <Box sx={{ p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dayBks.length === 0 ? (
                  <Typography variant="caption" color="text.disabled"
                    sx={{ display: 'block', textAlign: 'center', mt: 1.5, fontSize: '0.65rem' }}>
                    {profsWithSched.length > 0 ? 'Sin citas' : ''}
                  </Typography>
                ) : (
                  dayBks
                    .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                    .map((b) => (
                      <Tooltip
                        key={b.id}
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" display="block" fontWeight={700}>
                              {b.petName} · {b.ownerName}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {b.petSpecies} · {b.petBreed}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {b.specialtyName}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Tel: {b.ownerCelular}
                            </Typography>
                          </Box>
                        }
                        placement="right"
                        arrow
                        enterDelay={350}
                        componentsProps={{ tooltip: { sx: { maxWidth: 200, fontSize: '0.75rem' } } }}
                      >
                        <Box>
                          <BookingCard booking={b} compact />
                        </Box>
                      </Tooltip>
                    ))
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // ── VIEW: LIST ───────────────────────────────────────────────────────────────
  const renderListView = () => {
    const q = searchQuery.toLowerCase().trim();
    const upcoming = [...filtered]
      .filter((b) => b.date >= todayStr)
      .filter((b) => !q || [b.petName, b.ownerName, b.ownerCelular, b.specialtyName, b.professionalName, b.bookingCode]
        .some((v) => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));

    const grouped = {};
    upcoming.forEach((b) => {
      if (!grouped[b.date]) grouped[b.date] = [];
      grouped[b.date].push(b);
    });

    const handleExportCsv = () => {
      const rows = [
        ['Mascota','Especie','Raza','Propietario','Teléfono','Fecha','Hora','Veterinario','Especialidad','Estado','Fuente','Código'],
        ...upcoming.map(b => [
          b.petName, b.petSpecies, b.petBreed, b.ownerName, b.ownerCelular,
          b.date, b.time, b.professionalName, b.specialtyName,
          STATUS_CONFIG[b.status]?.label || b.status,
          b.source === 'online' ? 'Online' : b.source === 'agenda' ? 'Agenda' : 'Manual', b.bookingCode,
        ]),
      ];
      const csv  = rows.map(r => r.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `citas_${toDateStr(new Date())}.csv`;
      a.click(); URL.revokeObjectURL(url);
    };

    return (
      <Box>
        {/* Search bar */}
        <Box sx={{ p: 2, pb: 1, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth size="small"
            placeholder="Buscar por mascota, tutor, teléfono, especialidad, código…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ bgcolor: 'white', borderRadius: 2 }}
          />
          <Tooltip title="Exportar a CSV">
            <span>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon/>}
                onClick={handleExportCsv} disabled={upcoming.length === 0}
                sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}>CSV</Button>
            </span>
          </Tooltip>
        </Box>

        {Object.keys(grouped).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ListAltIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
            <Typography color="text.secondary">
              {searchQuery ? `Sin resultados para "${searchQuery}"` : 'No hay citas próximas.'}
            </Typography>
          </Box>
        ) : Object.entries(grouped).map(([dateStr, bks]) => {
          const d = new Date(dateStr + 'T12:00:00');
          const isToday = dateStr === todayStr;
          return (
            <Box key={dateStr} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 2, py: 1, bgcolor: isToday ? 'primary.main' : 'grey.100', borderRadius: 2 }}>
                <CalendarMonthIcon sx={{ color: isToday ? 'white' : 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isToday ? 'white' : 'text.primary' }}>
                  {isToday ? `Hoy · ${formatDate(d)}` : formatDate(d)}
                </Typography>
                <Chip label={`${bks.length} cita${bks.length > 1 ? 's' : ''}`} size="small"
                  sx={{ ml: 'auto', bgcolor: isToday ? 'rgba(255,255,255,0.25)' : 'white', color: isToday ? 'white' : 'primary.main', fontWeight: 600 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {bks.map((b) => {
                  const color = profColorMap[b.professionalId] || PROF_COLORS[0];
                  const status = STATUS_CONFIG[b.status] || STATUS_CONFIG.confirmed;
                  return (
                    <Paper key={b.id} variant="outlined" onClick={() => openDetail(b)} sx={{ p: 2, borderRadius: 3, cursor: 'pointer', borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'center', '&:hover': { boxShadow: 2 }, transition: 'box-shadow 0.15s' }}>
                      {/* Time + color bar */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 50 }}>
                        <Box sx={{ width: 4, flex: 1, bgcolor: color.bg, borderRadius: 2, mb: 0.5, minHeight: 40 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: color.text }}>{b.time}</Typography>
                      </Box>
                      {/* Pet avatar */}
                      <Avatar sx={{ bgcolor: color.light, color: color.bg, width: 44, height: 44, flexShrink: 0 }}>
                        <PetsIcon />
                      </Avatar>
                      {/* Main info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{b.petName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[b.petSpecies, b.petBreed].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">{b.ownerName} · {b.ownerCelular}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <MedicalServicesIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            Dr/a. {b.professionalName} · {b.specialtyName}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Status + code */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                        <Chip label={status.label} size="small" color={status.color} icon={status.icon} />
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>#{b.bookingCode}</Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ── VIEW: MONTH ───────────────────────────────────────────────────────────────
  const renderMonthView = () => {
    const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const firstDay = startOfWeek(monthStart);
    const days = Array.from({ length: 42 }, (_, i) => addDays(firstDay, i));

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', mb: 1 }}>
          {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map((d) => (
            <Typography key={d} variant="caption" fontWeight={700} color="text.secondary">{d}</Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
          {days.map((day) => {
            const ds = toDateStr(day);
            const inMonth = day.getMonth() === selectedDate.getMonth();
            const isToday = ds === todayStr;
            const dow = String(day.getDay());
            const dayBks = filtered.filter((b) => b.date === ds && ['confirmed','pending'].includes(b.status));
            const totalBookings = filtered.filter((b) => b.date === ds).length;
            const profRows = [];
            filteredProfessionals.forEach((p) => {
              rawSchedules.forEach((s) => {
                if (s.professionalId !== p.id || !s.clinicId || !s.specialtyId || !s.capacityId || !s.schedule) return;
                if (filterClinic && s.clinicId !== filterClinic) return;
                if (filterSpec && s.specialtyId !== filterSpec) return;
                if (filterCapacity && s.capacityId !== filterCapacity) return;
                if (filterBranch) {
                  const sb = s.branchId;
                  if (sb && sb !== filterBranch) return;
                  const clinic = clinics.find((c) => c.id === s.clinicId);
                  const cids = clinic?.branchIds || (clinic?.branchId ? [clinic.branchId] : []);
                  if (cids.length > 0 && !cids.includes(filterBranch)) return;
                }
                if (ds < s.startDate || (s.endDate && ds > s.endDate)) return;
                const daySched = s.schedule[dow];
                if (!daySched?.active) return;
                const slots = daySched.slots || [];
                const booked = dayBks.filter((b) =>
                  b.professionalId === p.id &&
                  (!filterClinic || b.clinicId === s.clinicId) &&
                  (!filterSpec || b.specialtyId === s.specialtyId) &&
                  (!filterCapacity || b.capacityId === s.capacityId)
                ).length;
                const avail = Math.max(0, slots.length - booked);
                const capacity = capacities.find((c) => c.id === s.capacityId);
                profRows.push({
                  id: p.id,
                  name: `Dr/a. ${p.nombre || ''} ${p.apellidos || ''}`,
                  capLabel: capacity ? `Consulta ${capacity.number}` : '',
                  avail,
                  total: slots.length,
                });
              });
            });

            return (
              <Paper
                key={ds}
                onClick={() => { setSelectedDate(day); setView('day'); }}
                sx={{
                  p: 1,
                  minHeight: 100,
                  borderRadius: 2,
                  cursor: 'pointer',
                  opacity: inMonth ? 1 : 0.4,
                  bgcolor: isToday ? 'primary.light' : 'background.paper',
                  '&:hover': { boxShadow: 2 },
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: isToday ? 'primary.contrastText' : 'text.primary' }}>
                    {day.getDate()}
                  </Typography>
                  {totalBookings > 0 && (
                    <Chip
                      label={totalBookings}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        bgcolor: isToday ? 'rgba(255,255,255,0.25)' : 'primary.main',
                        color: isToday ? 'primary.contrastText' : 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
                  {profRows.slice(0, 3).map((row, idx) => (
                    <Typography key={idx} variant="caption" noWrap sx={{ fontSize: '0.65rem', color: isToday ? 'primary.contrastText' : 'text.secondary' }}>
                      {row.name} · {row.avail} libre{row.avail !== 1 ? 's' : ''}
                    </Typography>
                  ))}
                  {profRows.length > 3 && (
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isToday ? 'primary.contrastText' : 'text.primary', fontWeight: 600 }}>
                      +{profRows.length - 3} más
                    </Typography>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ── Manual booking handlers ──────────────────────────────────────────────────────
  const handleOpenBookDlg = (date, time, prof, isSobrecupo = false) => {
    // Check if time has already passed for today
    const now = new Date();
    const selectedDateTime = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    selectedDateTime.setHours(hours, minutes, 0, 0);

    if (toDateStr(date) === todayStr && selectedDateTime < now) {
      setSnackbar({ open: true, message: 'No puedes agendar en horas que ya pasaron', severity: 'warning' });
      return;
    }

    const dayOfWeek = String(date.getDay());
    // Find schedule key for this professional on this day
    let specialtyId = '';
    let specialtyName = '';
    let clinicId = '';
    let branchId = '';
    let branchName = '';
    let capacityId = '';
    let scheduleId = '';
    // Collect all matching schedule keys for this professional on this day,
    // respecting the active clinic/specialty/capacity filters.
    const matchingKeys = [];
    Object.keys(profSchedules).forEach((key) => {
      const [pid, cid, sid, capid] = key.split('_');
      if (pid === prof.id &&
        (!filterClinic || cid === filterClinic) &&
        (!filterSpec || sid === filterSpec) &&
        (!filterCapacity || capid === filterCapacity) &&
        profSchedules[key]?.[dayOfWeek]?.active) {
        matchingKeys.push(key);
      }
    });
    if (matchingKeys.length === 1) {
      // Only one schedule: use it directly.
      const key = matchingKeys[0];
      const [pid, cid, sid, capid] = key.split('_');
      clinicId = cid;
      specialtyId = sid;
      capacityId = capid;
      scheduleId = key;
      specialtyName = specialties.find((s) => s.id === sid)?.name || '';
      branchId = scheduleBranchMap[key] || '';
      branchName = branches.find((b) => b.id === branchId)?.name || '';
    } else if (matchingKeys.length > 1) {
      // Multiple schedules: prefer the one whose slots include the clicked time.
      const slotKey = matchingKeys.find((key) => {
        const daySched = profSchedules[key]?.[dayOfWeek];
        return daySched?.active && (daySched.slots || []).some((s) => s.time === time);
      }) || matchingKeys[0];
      const [pid, cid, sid, capid] = slotKey.split('_');
      clinicId = cid;
      specialtyId = sid;
      capacityId = capid;
      scheduleId = slotKey;
      specialtyName = specialties.find((s) => s.id === sid)?.name || '';
      branchId = scheduleBranchMap[slotKey] || '';
      branchName = branches.find((b) => b.id === branchId)?.name || '';
    }
    // Fallback to filter if no schedule found
    if (!specialtyId && filterSpec) {
      const specialty = specialties.find((s) => s.id === filterSpec);
      specialtyId = specialty?.id || '';
      specialtyName = specialty?.name || '';
    }
    // Fallback branch from filter or global active branch
    if (!branchId) {
      branchId = filterBranch || activeBranchId || '';
      branchName = branches.find((b) => b.id === branchId)?.name || '';
    }
    // Never store 'all' as branchId; derive a real branch from the professional's clinics
    if (branchId === 'all' || !branchId) {
      const profClinicIds = prof.clinicIds || (prof.clinicId ? [prof.clinicId] : []);
      for (const cid of profClinicIds) {
        const c = clinics.find((cl) => cl.id === cid);
        const cBranches = c?.branchIds || (c?.branchId ? [c.branchId] : []);
        if (filterBranch && cBranches.includes(filterBranch)) {
          branchId = filterBranch;
          branchName = branches.find((b) => b.id === branchId)?.name || '';
          break;
        } else if (cBranches[0]) {
          branchId = cBranches[0];
          branchName = branches.find((b) => b.id === branchId)?.name || '';
          break;
        }
      }
    }
    setBookDlg({
      open: true,
      date,
      time,
      profId: prof.id,
      profName: `${prof.nombre} ${prof.apellidos}`,
      specialtyId,
      specialtyName,
      clinicId,
      branchId,
      branchName,
      capacityId,
      scheduleId,
      sobrecupo: isSobrecupo,
    });
    setDlgError('');
  };

  const resetDlg = () => {
    setBookDlg({ open: false, date: null, time: '', profId: '', profName: '', specialtyId: '', specialtyName: '', clinicId: '', branchId: '', branchName: '', capacityId: '', scheduleId: '', sobrecupo: false });
    setDlgOwner({ name: '', rut: '', celular: '', mail: '', convenio: '' });
    setDlgOwnerId(null); setDlgOwnerFound(false); setDlgOwnerPets([]);
    setDlgPetMode('new'); setDlgSelectedPet(null);
    setDlgPetData({ name: '', species: '', breed: '', birthDate: '', genero: '' });
    setDlgError(''); setDlgSaving(false);
    setDlgAppointmentType(''); setDlgDurationMin(20);
    setDlgMotivoConsulta(''); setDlgInternalNotes(''); setDlgNotas('');
    setDlgNoShowCount(0); setDlgTotalBookings(0);
    setDlgSearchMode('rut'); setDlgSearchPhone('');
  };

  const handleDlgRutSearch = async () => {
    if (!dlgOwner.rut) return;
    if (!validateRut(dlgOwner.rut)) { setDlgError('RUT inválido'); return; }
    setDlgRutSearching(true); setDlgError('');
    try {
      const found = await findDocumentsByField('owners', 'rut', dlgOwner.rut);
      if (found?.length > 0) {
        const o = found[0];
        setDlgOwner({ name: o.name || '', rut: o.rut || '', celular: o.celular || '', mail: o.contact || o.mail || '', convenio: o.convenio || '' });
        setDlgOwnerId(o.id); setDlgOwnerFound(true);
        setDlgPetsLoading(true);
        const pets = await findDocumentsByField('patients', 'ownerId', o.id);
        setDlgOwnerPets(pets || []);
        setDlgPetMode(pets?.length > 0 ? 'select' : 'new');
        if (pets?.length > 0) {
          setDlgSelectedPet(pets[0]);
          // Count no-shows for first pet
          const petId = pets[0].id;
          const petBks = bookings.filter(b => b.petId === petId);
          setDlgNoShowCount(petBks.filter(b => b.status === 'no_show').length);
          setDlgTotalBookings(petBks.length);
        }
      } else {
        setDlgOwnerFound(false); setDlgOwnerPets([]); setDlgPetMode('new');
      }
    } catch (e) { console.error(e); }
    finally { setDlgRutSearching(false); setDlgPetsLoading(false); }
  };

  const handleDlgPhoneSearch = async () => {
    if (!dlgSearchPhone) return;
    setDlgRutSearching(true); setDlgError('');
    try {
      const found = await findDocumentsByField('owners', 'celular', dlgSearchPhone.replace(/\D/g, ''));
      if (found?.length > 0) {
        const o = found[0];
        setDlgOwner({ name: o.name || '', rut: o.rut || '', celular: o.celular || '', mail: o.contact || o.mail || '', convenio: o.convenio || '' });
        setDlgOwnerId(o.id); setDlgOwnerFound(true);
        setDlgPetsLoading(true);
        const pets = await findDocumentsByField('patients', 'ownerId', o.id);
        setDlgOwnerPets(pets || []);
        setDlgPetMode(pets?.length > 0 ? 'select' : 'new');
        if (pets?.length > 0) setDlgSelectedPet(pets[0]);
      } else {
        setDlgError('No se encontró tutor con ese teléfono');
      }
    } catch { setDlgError('Error al buscar'); }
    finally { setDlgRutSearching(false); setDlgPetsLoading(false); }
  };

  const handleDlgSubmit = async () => {
    if (!bookDlg.specialtyId) { setDlgError('Especialidad no disponible'); return; }
    if (!dlgOwner.name.trim() || !dlgOwner.rut.trim() || !dlgOwner.celular.trim()) {
      setDlgError('Completa nombre, RUT y celular del tutor'); return;
    }
    const petName    = dlgPetMode === 'select' ? dlgSelectedPet?.name    : dlgPetData.name;
    const petSpecies = dlgPetMode === 'select' ? dlgSelectedPet?.species : dlgPetData.species;
    if (!petName || !petSpecies) { setDlgError('Completa los datos de la mascota'); return; }
    const prof = professionals.find((p) => p.id === bookDlg.profId);
    const ownerRut = normalizeRut(dlgOwner.rut) || dlgOwner.rut;
    const petBreed = dlgPetMode === 'select' ? (dlgSelectedPet?.breed || '') : dlgPetData.breed;
    const petGender = dlgPetMode === 'select' ? (dlgSelectedPet?.genero || '') : dlgPetData.genero;
    const petBirthDate = dlgPetMode === 'select' ? (dlgSelectedPet?.birthDate || '') : dlgPetData.birthDate;
    let ownerId = dlgOwnerId;
    let petId = dlgPetMode === 'select' ? (dlgSelectedPet?.id || null) : null;
    setDlgSaving(true); setDlgError('');
    try {
      if (!ownerId) {
        const existingOwners = await findDocumentsByField('owners', 'rut', ownerRut);
        if (existingOwners?.length > 0) {
          ownerId = existingOwners[0].id;
          await updateDocument('owners', ownerId, {
            convenio: dlgOwner.convenio || '',
            updatedAt: new Date(),
          });
        } else {
          ownerId = await addDocument('owners', {
            name: dlgOwner.name.trim(),
            rut: ownerRut,
            celular: dlgOwner.celular.trim(),
            mail: dlgOwner.mail.trim() || '',
            contact: dlgOwner.mail.trim() || '',
            convenio: dlgOwner.convenio || '',
            createdAt: new Date(),
          });
        }
      }
      if (dlgPetMode === 'new' && !petId) {
        petId = await addDocument('patients', {
          name: petName.trim(),
          species: petSpecies,
          breed: petBreed,
          genero: petGender,
          birthDate: petBirthDate,
          ownerId,
          rutTutor: ownerRut,
          branchIds: bookDlg.branchId && bookDlg.branchId !== 'all' ? [bookDlg.branchId] : [],
          createdAt: new Date(),
        });
      }
      const convenio = await resolverConvenio({ ownerId, convenioName: dlgOwner.convenio });
      const consultationPrice = await getConsultationPrice(bookDlg.specialtyId, bookDlg.branchId, convenio);
      const code = await createBooking({
        specialtyId: bookDlg.specialtyId,
        specialtyName: bookDlg.specialtyName || '',
        professionalId: bookDlg.profId,
        professionalName: `${prof?.nombre || ''} ${prof?.apellidos || ''}`,
        clinicId: bookDlg.clinicId || prof?.clinicId || (prof?.clinicIds ? prof.clinicIds[0] : ''),
        clinicName: clinics.find((c) => c.id === (bookDlg.clinicId || prof?.clinicId || (prof?.clinicIds ? prof.clinicIds[0] : '')))?.name || prof?.clinicName || '',
        branchId: bookDlg.branchId || '',
        branchName: bookDlg.branchName || '',
        capacityId: bookDlg.capacityId || '',
        scheduleId: bookDlg.scheduleId || '',
        date: toDateStr(bookDlg.date),
        time: bookDlg.time,
        timeLabel: bookDlg.time,
        ownerId,
        ownerName: dlgOwner.name,
        ownerRut: ownerRut,
        ownerCelular: dlgOwner.celular,
        ownerMail: dlgOwner.mail || '',
        ownerConvenio: dlgOwner.convenio || '',
        petName,
        petSpecies,
        petBreed,
        petGender,
        petAge: dlgPetMode === 'select' ? (String(dlgSelectedPet?.age || '')) : '',
        petId,
        title: `${petName} - ${bookDlg.specialtyName || ''}`,
        sobrecupo: bookDlg.sobrecupo || false,
        source: 'agenda',
        appointmentType: dlgAppointmentType || '',
        motivoConsulta: dlgMotivoConsulta || '',
        estimatedDurationMin: dlgDurationMin || 20,
        internalNotes: dlgInternalNotes || '',
        notas: dlgNotas || '',
        amount: consultationPrice,
        paymentStatus: consultationPrice > 0 ? 'no_pagado' : 'pagado',
        status: 'no_confirmado',
      });
      // Link budget to the new booking
      const qBooking = query(collection(db, 'onlineBookings'), where('bookingCode', '==', code));
      const bSnap = await getDocs(qBooking);
      const bookingDoc = bSnap.docs[0];
      if (bookingDoc && consultationPrice > 0) {
        try {
          const consultaArancel = await getConsultationArancel(bookDlg.specialtyId, bookDlg.branchId);
          const esGeneral = /general|medicina general|clinica general/i.test(bookDlg.specialtyName || '');
          const prestacionFallback = esGeneral ? 'Consulta Médica' : `Consulta ${bookDlg.specialtyName || ''}`.trim();
          const tipoPrestacionFallback = esGeneral ? 'Consulta Médica' : 'Consulta Especialidad';
          await crearPresupuestoAutomatico({
            orderId: bookingDoc.id,
            orderType: 'onlineBooking',
            orderNumber: code,
            patientId: petId || '',
            patientName: petName,
            patientSpecies: petSpecies,
            ownerId: ownerId || '',
            ownerName: dlgOwner.name,
            ownerPhone: dlgOwner.celular,
            convenioName: dlgOwner.convenio || '',
            branchId: bookDlg.branchId || '',
            vetId: bookDlg.profId || '',
            vetName: bookDlg.profName || `${prof?.nombre || ''} ${prof?.apellidos || ''}`.trim(),
            items: [{
              arancel: consultaArancel,
              nombre: prestacionFallback,
              cantidad: 1,
              precioFallback: consultationPrice,
              tipoPrestacionFallback,
            }],
            notas: `Generado desde cita agenda ${code}`,
            updateOrderCollection: 'onlineBookings',
          });
          await updateDoc(bookingDoc.ref, {
            arancelId: consultaArancel?.id || '',
            prestacion: consultaArancel?.prestacion || consultaArancel?.nombre || prestacionFallback,
            tipoPrestacionId: consultaArancel?.tipoPrestacionId || '',
            tipoPrestacionName: consultaArancel?.tipoPrestacionName || consultaArancel?.tipoPrestacion || tipoPrestacionFallback,
          });
        } catch (err) {
          console.error('Error creando presupuesto de la cita:', err);
        }
      }
      setSnackbar({ open: true, message: `Cita creada · Código: ${code}`, severity: 'success' });

      // Meta Pixel: Lead desde Conversions API (no expone IP del usuario de la plataforma)
      trackMetaEventServer({
        eventName: 'Lead',
        value: 0,
        currency: 'CLP',
        owner: { name: dlgOwner.name, rut: ownerRut, celular: dlgOwner.celular, mail: dlgOwner.mail, ownerId },
        itemName: bookDlg.specialtyName || 'Agendamiento',
        contentIds: [code],
        eventId: `lead_${code}`,
      }).catch(() => {});

      resetDlg();
    } catch (err) {
      setDlgError(err.message || 'Error al crear la cita');
    } finally { setDlgSaving(false); }
  };

  // ── Manual Booking Dialog ──────────────────────────────────────────────────────
  const renderBookingDialog = () => {
    const prof  = professionals.find((p) => p.id === bookDlg.profId);
    const color = profColorMap[bookDlg.profId] || PROF_COLORS[0];
    return (
      <Dialog open={bookDlg.open} onClose={resetDlg} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: color.bg, px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>Nueva Cita</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Dr/a. {bookDlg.profName} · {bookDlg.date ? formatDateShort(bookDlg.date) : ''} · {bookDlg.time}
          </Typography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {dlgError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDlgError('')}>{dlgError}</Alert>}

          {bookDlg.specialtyName && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#F3F4F6', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Especialidad</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{bookDlg.specialtyName}</Typography>
            </Box>
          )}

          <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">DATOS DEL TUTOR</Typography></Divider>

          {/* Search mode tabs */}
          <Tabs value={dlgSearchMode} onChange={(_, v) => setDlgSearchMode(v)} sx={{ mb: 1.5, minHeight: 32 }}
            TabIndicatorProps={{ style: { height: 2 } }}>
            <Tab value="rut" label="Por RUT" sx={{ fontSize: '0.75rem', minHeight: 32, py: 0.5 }}/>
            <Tab value="celular" label="Por teléfono" sx={{ fontSize: '0.75rem', minHeight: 32, py: 0.5 }}/>
          </Tabs>

          {dlgSearchMode === 'rut' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField label="RUT del tutor" value={dlgOwner.rut} size="small" fullWidth
                placeholder="12345678-9"
                onChange={(e) => { setDlgOwner((p) => ({ ...p, rut: e.target.value })); setDlgOwnerFound(false); }}
                onKeyDown={(e) => e.key === 'Enter' && handleDlgRutSearch()}/>
              <Button variant="outlined" onClick={handleDlgRutSearch} disabled={dlgRutSearching}
                sx={{ whiteSpace: 'nowrap', minWidth: 80 }} startIcon={dlgRutSearching ? <CircularProgress size={14}/> : <SearchIcon/>}>
                Buscar
              </Button>
            </Box>
          )}
          {dlgSearchMode === 'celular' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField label="Celular" value={dlgSearchPhone} size="small" fullWidth
                placeholder="+56 9 XXXX XXXX"
                onChange={(e) => setDlgSearchPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDlgPhoneSearch()}/>
              <Button variant="outlined" onClick={handleDlgPhoneSearch} disabled={dlgRutSearching}
                sx={{ whiteSpace: 'nowrap', minWidth: 80 }} startIcon={dlgRutSearching ? <CircularProgress size={14}/> : <SearchIcon/>}>
                Buscar
              </Button>
            </Box>
          )}
          {dlgOwnerFound && <Alert severity="success" icon={false} sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>Tutor encontrado en el sistema</Alert>}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={12}>
              <TextField label="Nombre" value={dlgOwner.name} size="small" fullWidth required
                onChange={(e) => setDlgOwner((p) => ({ ...p, name: e.target.value }))} disabled={dlgOwnerFound} />
            </Grid>
            <Grid size={6}>
              <TextField label="Celular" value={dlgOwner.celular} size="small" fullWidth required
                onChange={(e) => setDlgOwner((p) => ({ ...p, celular: e.target.value }))} disabled={dlgOwnerFound} />
            </Grid>
            <Grid size={6}>
              <TextField label="Email" value={dlgOwner.mail} size="small" fullWidth type="email"
                onChange={(e) => setDlgOwner((p) => ({ ...p, mail: e.target.value }))} disabled={dlgOwnerFound} />
            </Grid>
            <Grid size={6}>
              <TextField label="Convenio" value={dlgOwner.convenio} size="small" fullWidth select
                onChange={(e) => setDlgOwner((p) => ({ ...p, convenio: e.target.value }))} disabled={dlgOwnerFound}
                helperText={convenios.length === 0 ? 'Sin convenios configurados' : undefined}>
                <MenuItem value=""><em>Sin convenio</em></MenuItem>
                {convenios
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((c) => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          {/* No-show alert */}
          {dlgNoShowCount >= 2 && (
            <Alert severity={dlgNoShowCount >= 5 ? 'error' : 'warning'}
              sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>
              {dlgNoShowCount >= 5
                ? `⚠ Alto historial de ausencias: ${dlgNoShowCount} no-shows en ${dlgTotalBookings} citas`
                : `${dlgNoShowCount} no-shows registrados para esta mascota`}
            </Alert>
          )}

          <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">MASCOTA</Typography></Divider>

          {dlgPetsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={20} /></Box>}

          {!dlgPetsLoading && dlgOwnerPets.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Selecciona una mascota registrada o agrega nueva</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {dlgOwnerPets.map((pet) => (
                  <Chip key={pet.id}
                    icon={<PetsIcon />}
                    label={`${pet.name}${pet.species ? ` · ${pet.species}` : ''}`}
                    onClick={() => { setDlgSelectedPet(pet); setDlgPetMode('select'); }}
                    color={dlgPetMode === 'select' && dlgSelectedPet?.id === pet.id ? 'primary' : 'default'}
                    variant={dlgPetMode === 'select' && dlgSelectedPet?.id === pet.id ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
                <Chip
                  icon={<AddCircleOutlineIcon />}
                  label="Nueva mascota"
                  onClick={() => { setDlgPetMode('new'); setDlgSelectedPet(null); }}
                  color={dlgPetMode === 'new' ? 'primary' : 'default'}
                  variant={dlgPetMode === 'new' ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          )}

          {/* ── Detalles de la cita ── */}
          <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">DETALLES DE LA CITA</Typography></Divider>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField select label="Tipo de cita" value={dlgAppointmentType} size="small" fullWidth
                onChange={e => setDlgAppointmentType(e.target.value)}>
                <MenuItem value="">Sin especificar</MenuItem>
                <MenuItem value="primera_consulta">Primera consulta (45 min)</MenuItem>
                <MenuItem value="control">Control / seguimiento (20 min)</MenuItem>
                <MenuItem value="vacuna">Vacunación (15 min)</MenuItem>
                <MenuItem value="urgencia">Urgencia</MenuItem>
                <MenuItem value="telemedicina">Telemedicina (30 min)</MenuItem>
                <MenuItem value="otro">Otro</MenuItem>
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField select label="Duración estimada" value={dlgDurationMin} size="small" fullWidth
                onChange={e => setDlgDurationMin(e.target.value)}>
                {[15,20,30,45,60].map(m => <MenuItem key={m} value={m}>{m} minutos</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField label="Motivo de consulta" value={dlgMotivoConsulta} size="small" fullWidth multiline rows={2}
                placeholder="¿Por qué trae a su mascota hoy?" inputProps={{ maxLength: 200 }}
                onChange={e => setDlgMotivoConsulta(e.target.value)}
                helperText={`${dlgMotivoConsulta.length}/200`}/>
            </Grid>
            <Grid size={12}>
              <TextField label="Notas internas (solo staff)" value={dlgInternalNotes} size="small" fullWidth
                placeholder="Ej: Requiere bozal · Paciente ansioso · Llama antes"
                onChange={e => setDlgInternalNotes(e.target.value)}
                sx={{ bgcolor: '#FFFBEB', '& .MuiInputLabel-root': { color: '#92400E' } }}/>
            </Grid>
            <Grid size={12}>
              <TextField label="Notas de la cita" value={dlgNotas} size="small" fullWidth multiline rows={2}
                placeholder="Información adicional visible en la tarjeta"
                onChange={e => setDlgNotas(e.target.value)}/>
            </Grid>
          </Grid>

          {!dlgPetsLoading && (dlgOwnerPets.length === 0 || dlgPetMode === 'new') && (
            <Grid container spacing={1.5}>
              <Grid size={12}>
                <TextField label="Nombre mascota" value={dlgPetData.name} size="small" fullWidth required
                  onChange={(e) => setDlgPetData((p) => ({ ...p, name: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PetsIcon fontSize="small" /></InputAdornment> }}
                />
              </Grid>
              <Grid size={6}>
                <TextField select label="Especie" value={dlgPetData.species} size="small" fullWidth required
                  onChange={(e) => setDlgPetData((p) => ({ ...p, species: e.target.value, breed: '' }))}>
                  <MenuItem value="">Seleccionar...</MenuItem>
                  <MenuItem value="Canino">Canino</MenuItem>
                  <MenuItem value="Felino">Felino</MenuItem>
                </TextField>
              </Grid>
              <Grid size={6}>
                <SearchableSelect label="Raza" value={dlgPetData.breed}
                  disabled={!dlgPetData.species}
                  onChange={(e) => setDlgPetData((p) => ({ ...p, breed: e.target.value }))}
                  options={dlgBreeds
                    .filter(b => b.species === dlgPetData.species)
                    .map(b => ({ value: b.name, label: b.name }))}
                  emptyLabel="Sin especificar" />
              </Grid>
              <Grid size={6}>
                <TextField label="Fecha de nacimiento" type="date" value={dlgPetData.birthDate} size="small" fullWidth
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setDlgPetData((p) => ({ ...p, birthDate: e.target.value }))} />
              </Grid>
              <Grid size={6}>
                <TextField select label="Sexo" value={dlgPetData.genero} size="small" fullWidth
                  onChange={(e) => setDlgPetData((p) => ({ ...p, genero: e.target.value }))}>
                  <MenuItem value="">Sin especificar</MenuItem>
                  <MenuItem value="Macho">Macho</MenuItem>
                  <MenuItem value="Hembra">Hembra</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={resetDlg} sx={{ borderRadius: 3 }}>Cancelar</Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={handleDlgSubmit} disabled={dlgSaving}
            sx={{ borderRadius: 3, bgcolor: color.bg, '&:hover': { bgcolor: color.text } }}>
            {dlgSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirmar Cita'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ── Detail Dialog ─────────────────────────────────────────────────────────────
  const renderDetailDialog = () => {
    if (!selected) return null;
    const color         = profColorMap[selected.professionalId] || PROF_COLORS[0];
    const currentStatus = bookings.find((b) => b.id === selected.id)?.status || selected.status;
    const status        = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.confirmed;
    const branch        = branches.find((b) => b.id === selected.branchId);

    // Patient history from bookings
    const petHistory  = bookings.filter(b => b.petId && b.petId === selected.petId && b.id !== selected.id);
    const petNoShows  = petHistory.filter(b => b.status === 'no_show').length;
    const lastVisit   = petHistory
      .filter(b => b.status === 'completed' && b.date < selected.date)
      .sort((a, b) => b.date.localeCompare(a.date))[0];

    // D1: Link de confirmación de cita (página pública /confirmar/:code)
    const confirmUrl = `${window.location.origin}/confirmar/${selected.bookingCode || ''}`;
    const confirmText = `Hola ${selected.ownerName}, confirma tu cita para ${selected.petName} el ${selected.date} a las ${selected.time} con Dr/a. ${selected.professionalName}. Confirma o cancela aquí: ${confirmUrl}`;
    const confirmWaUrl = `https://wa.me/56${(selected.ownerCelular || '').replace(/\D/g, '')}?text=${encodeURIComponent(confirmText)}`;

    const APPT_LABELS = { primera_consulta: 'Primera consulta', control: 'Control', vacuna: 'Vacunación', urgencia: 'Urgencia', telemedicina: 'Telemedicina' };

    return (
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        {/* ─ Header con color del profesional */}
        <Box sx={{ bgcolor: color.bg, px: 3, py: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>{selected.petName}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {selected.date} · {selected.time} · Dr/a. {selected.professionalName}
              </Typography>
            </Box>
            <Stack alignItems="flex-end" spacing={0.5}>
              <Chip label={status.label} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }}/>
              {selected.arrivedAt && (
                <Chip label="● En sala" size="small"
                  sx={{ bgcolor: 'rgba(16,185,129,0.35)', color: 'white', fontSize: '0.65rem',
                    '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.6 } },
                    animation: 'pulse 2s infinite' }}/>
              )}
            </Stack>
          </Stack>
        </Box>
        <DialogContent sx={{ p: 0 }}>

          {/* ─ Sección 1: El paciente */}
          <Box sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0' }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Avatar sx={{ bgcolor: color.light, color: color.bg, width: 48, height: 48 }}><PetsIcon/></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="body1" fontWeight={700}>{selected.petName}</Typography>
                  {petNoShows >= 2 && (
                    <Chip label={`${petNoShows} no-shows`} size="small"
                      sx={{ height: 18, fontSize: '0.6rem',
                        bgcolor: petNoShows >= 5 ? '#FEE2E2' : '#FEF3C7',
                        color: petNoShows >= 5 ? '#B91C1C' : '#92400E' }}/>
                  )}
                  {selected.petId && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<PersonIcon/>}
                      onClick={openEditPatient}
                      sx={{ fontSize: '0.7rem', py: 0.2 }}
                    >
                      Editar
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {[selected.petSpecies, selected.petBreed, selected.petAge ? `${selected.petAge} años` : ''].filter(Boolean).join(' · ')}
                </Typography>
                <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <PersonIcon sx={{ fontSize: 12 }}/> {selected.ownerName}
                    {selected.ownerRut && <span style={{ color: '#94A3B8', marginLeft: 4, fontFamily: 'monospace' }}>{selected.ownerRut}</span>}
                  </Typography>
                  {selected.ownerCelular && (
                    <Typography variant="caption" component="a" href={`tel:${selected.ownerCelular}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: 'primary.main', textDecoration: 'none' }}>
                      <PhoneIcon sx={{ fontSize: 12 }}/> {selected.ownerCelular}
                    </Typography>
                  )}
                </Stack>
                {lastVisit && (
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.3 }}>
                    Última visita: {lastVisit.date} · {lastVisit.specialtyName || ''}
                  </Typography>
                )}
                {selected.petId && (
                  <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
                    <TextField
                      label="Peso (kg)"
                      size="small"
                      type="number"
                      value={detailWeight}
                      onChange={(e) => setDetailWeight(e.target.value)}
                      sx={{ width: 120 }}
                      disabled={detailWeightSaving}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={detailWeightSaving}
                      onClick={async () => {
                        setDetailWeightSaving(true);
                        try {
                          const weightValue = Number(detailWeight);
                          const vetName = userData?.name ||
                            `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim() ||
                            userData?.email || '';
                          const now = new Date();
                          // 1) Add weight to the patient's subcollection for history
                          await addDoc(collection(db, 'patients', selected.petId, 'weights'), {
                            weight: weightValue,
                            date: now,
                            createdAt: now,
                            vetNombre: vetName,
                            professionalId: selected.professionalId || '',
                            source: 'appointment',
                          });
                          // 2) Keep the patient profile's current weight in sync
                          await updateDocument('patients', selected.petId, { weight: weightValue });
                          setSnackbar({ open: true, message: 'Peso guardado en ficha del paciente', severity: 'success' });
                        } catch {
                          setSnackbar({ open: true, message: 'Error al guardar el peso', severity: 'error' });
                        } finally {
                          setDetailWeightSaving(false);
                        }
                      }}
                    >
                      {detailWeightSaving ? <CircularProgress size={16}/> : 'Guardar'}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>

          {/* ─ Sección 2: Detalles de la cita */}
          <Box sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0' }}>
            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="caption" color="text.disabled">Profesional</Typography>
                <Typography variant="body2" fontWeight={600}>Dr/a. {selected.professionalName}</Typography>
              </Grid>
              {selected.specialtyName && (
                <Grid size={6}>
                  <Typography variant="caption" color="text.disabled">Especialidad</Typography>
                  <Typography variant="body2" fontWeight={600}>{selected.specialtyName}</Typography>
                </Grid>
              )}
              {selected.appointmentType && (
                <Grid size={6}>
                  <Typography variant="caption" color="text.disabled">Tipo de cita</Typography>
                  <Typography variant="body2">{APPT_LABELS[selected.appointmentType] || selected.appointmentType}</Typography>
                </Grid>
              )}
              {branch && (
                <Grid size={6}>
                  <Typography variant="caption" color="text.disabled">Sucursal</Typography>
                  <Typography variant="body2">{branch.name}</Typography>
                </Grid>
              )}
              <Grid size={6}>
                <Typography variant="caption" color="text.disabled">Código</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>#{selected.bookingCode}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.disabled">Fuente</Typography>
                <Typography variant="body2">{selected.source === 'online' ? 'Online (Web)' : selected.source === 'agenda' ? 'Agenda' : 'Manual (clínica)'}</Typography>
              </Grid>
              {selected.motivoConsulta && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.disabled">Motivo de consulta</Typography>
                  <Typography variant="body2">{selected.motivoConsulta}</Typography>
                </Grid>
              )}
              {selected.internalNotes && (
                <Grid size={12}>
                  <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem', mt: 0.5 }}>
                    <strong>Nota interna:</strong> {selected.internalNotes}
                  </Alert>
                </Grid>
              )}
              <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                  <TextField
                    label="Notas de la cita"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={detailNotas}
                    onChange={(e) => setDetailNotas(e.target.value)}
                    disabled={detailNotasSaving}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={detailNotasSaving}
                    onClick={async () => {
                      setDetailNotasSaving(true);
                      try {
                        await updateDocument('onlineBookings', selected.id, { notas: detailNotas });
                        setSnackbar({ open: true, message: 'Notas guardadas', severity: 'success' });
                      } catch {
                        setSnackbar({ open: true, message: 'Error al guardar notas', severity: 'error' });
                      } finally {
                        setDetailNotasSaving(false);
                      }
                    }}
                  >
                    {detailNotasSaving ? <CircularProgress size={16}/> : 'Guardar'}
                  </Button>
                </Box>
              </Grid>
              {(() => {
                const pay = getBookingPaymentInfo(selected);
                if (!pay.hasPayment) return null;
                return (
                  <Grid size={12}>
                    <Alert
                      severity={pay.paid ? 'success' : 'warning'}
                      icon={pay.paid ? <CheckCircleIcon fontSize="small"/> : undefined}
                      sx={{ py: 0.5, fontSize: '0.78rem' }}
                    >
                      {pay.isWebpay ? (
                        <>Pago Webpay: {formatPrice(pay.amount)}{pay.auth ? ` · Aut: ${pay.auth}` : ''} · {pay.label}</>
                      ) : pay.isSinCosto ? (
                        <>Cita sin costo{pay.convenioName ? ` · Convenio: ${pay.convenioName}` : ''}</>
                      ) : pay.prestacion ? (
                        <>{pay.prestacion}: {formatPrice(pay.unit)} · A pagar: {formatPrice(pay.amount)} · {pay.label}</>
                      ) : (
                        <>A pagar: {formatPrice(pay.amount)} · {pay.label}</>
                      )}
                    </Alert>
                  </Grid>
                );
              })()}
            </Grid>
          </Box>

          {/* ─ Sección 3: Acciones */}
          <Box sx={{ p: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid #E2E8F0' }}>
            {/* Cambiar estado */}
            {STATUS_ACTIONS.filter(a => a.to !== currentStatus).map(a => (
              <Button key={a.to} size="small" variant="outlined"
                color={a.color === 'default' ? 'inherit' : a.color}
                onClick={() => handleStatusChange(a.to)} disabled={updating}
                startIcon={updating ? <CircularProgress size={12}/> : STATUS_CONFIG[a.to]?.icon}
                sx={{ borderRadius: 3, fontSize: '0.75rem' }}>
                {a.label}
              </Button>
            ))}

            {/* Reagendar */}
            {['confirmed', 'pending', 'en_sala'].includes(currentStatus) && (
              <Button variant="outlined" color="primary" size="small"
                startIcon={<CalendarMonthIcon/>}
                onClick={() => openReagendar(selected)}>
                Reagendar
              </Button>
            )}

            {/* Reasignar médico */}
            {['confirmed', 'pending', 'en_sala'].includes(currentStatus) && (
              <Button variant="outlined" color="info" size="small"
                startIcon={<MedicalServicesIcon/>}
                onClick={() => openReassign(selected)}>
                Reasignar médico
              </Button>
            )}

            {/* Paciente llegó */}
            {!selected.arrivedAt && ['confirmed', 'pending', 'en_sala'].includes(currentStatus) && (
              <Button variant="outlined" color="success" size="small"
                startIcon={<CheckCircleIcon/>}
                onClick={async () => {
                  await updateDocument('onlineBookings', selected.id, { arrivedAt: new Date(), status: 'en_sala' });
                  setSelected(prev => ({ ...prev, arrivedAt: new Date(), status: 'en_sala' }));
                  setSnackbar({ open: true, message: 'Paciente marcado como llegado ✓', severity: 'success' });
                }}>
                Paciente llegó
              </Button>
            )}

            {/* D1: Enviar link de confirmación de cita al paciente */}
            {selected.ownerCelular && selected.bookingCode && (
              <Button variant="outlined" size="small" color="primary"
                startIcon={<WhatsAppIcon/>}
                component="a" href={confirmWaUrl} target="_blank"
                onClick={async () => {
                  await updateDocument('onlineBookings', selected.id, {
                    confirmationSentAt: new Date(),
                    confirmationSentBy: userData?.name || userData?.email,
                  });
                  setSelected(prev => ({ ...prev, confirmationSentAt: new Date() }));
                }}>
                {selected.confirmationSentAt ? 'Reenviar confirmación' : 'Enviar confirmación'}
              </Button>
            )}
          </Box>

          {/* ─ Links rápidos */}
          {selected.petId && (
            <Box sx={{ px: 2.5, py: 1.5 }}>
              <Button size="small" variant="text" startIcon={<LocalHospitalIcon/>}
                onClick={() => window.open(`/patients/${selected.petId}/record`, '_blank')}>
                Ver ficha clínica
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Box sx={{ flex: 1 }}/>
          <Button variant="outlined" onClick={() => setDetailOpen(false)} sx={{ borderRadius: 3 }}>Cerrar</Button>
        </DialogActions>

        <PatientOwnerDialog
          open={editPatientOpen}
          onClose={() => setEditPatientOpen(false)}
          initial={editPatientInitial}
          onSaved={async () => {
            try {
              const [p, o] = await Promise.all([getCollection('patients'), getCollection('owners')]);
              setPatients(p);
              setOwners(o);
              setSnackbar({ open: true, message: 'Paciente actualizado', severity: 'success' });
            } catch {
              setSnackbar({ open: true, message: 'Error recargando pacientes', severity: 'error' });
            }
          }}
        />
      </Dialog>
    );
  };

  // ── Reagendar dialog ─────────────────────────────────────────────────────────
  const reagendarDialog = reagendarOpen && (
    <Dialog open={reagendarOpen} onClose={() => setReagendarOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Reagendar cita</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            type="date" label="Nueva fecha" value={reagendarDate}
            onChange={(e) => { setReagendarDate(e.target.value); setReagendarTime(''); }}
            InputLabelProps={{ shrink: true }} fullWidth
          />
          <TextField
            select label="Nuevo horario" value={reagendarTime}
            onChange={(e) => setReagendarTime(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth
            disabled={reagendarLoading || !reagendarDate || reagendarSlots.length === 0}
          >
            {reagendarSlots.map((slot) => {
              const cuposDisp = (slot.capacity || 1) - (slot.booked || 0);
              return (
              <MenuItem key={slot.time} value={slot.time}>
                {slot.time} · {cuposDisp} cupo{cuposDisp !== 1 ? 's' : ''}
              </MenuItem>
              );
            })}
          </TextField>
          {reagendarLoading && <CircularProgress size={20} />}
          {!reagendarLoading && reagendarDate && reagendarSlots.length === 0 && (
            <Alert severity="info" sx={{ py: 0 }}>No hay horarios disponibles para esta fecha.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReagendarOpen(false)}>Cancelar</Button>
        <Button variant="contained" onClick={handleReagendarSave} disabled={!reagendarDate || !reagendarTime}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Reasignar médico dialog ─────────────────────────────────────────────────
  const reassignDialog = reassignOpen && (
    <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Reasignar médico</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Mover la cita del {selected?.date} a las {selected?.time} a otra agenda.
          </Typography>
          <SearchableSelect
            label="Nuevo médico"
            value={reassignProfId}
            onChange={(e) => setReassignProfId(e.target.value)}
            options={professionals.map((p) => ({
              value: p.id,
              label: `Dr/a. ${p.nombre} ${p.apellidos || ''}`.trim(),
            }))}
            emptyLabel="Seleccionar..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReassignOpen(false)}>Cancelar</Button>
        <Button variant="contained" onClick={handleReassignSave} disabled={!reassignProfId || reassignProfId === selected?.professionalId || reassignSaving}>
          {reassignSaving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Header label ─────────────────────────────────────────────────────────────
  const headerLabel = view === 'week'
    ? `${formatDateShort(weekDays[0])} – ${formatDateShort(weekDays[6])} ${weekDays[6].getFullYear()}`
    : view === 'month'
    ? `${MONTH_NAMES[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
    : view === 'list'
    ? 'Próximas Citas'
    : formatDate(selectedDate);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', p: { xs: 1, md: 2 } }}>
      {/* ── Stats bar ── */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        {[
          { label: `Citas ${statsLabel}`,   value: stats.total,     color: 'primary.main',  icon: <CalendarMonthIcon /> },
          { label: 'Por atender',           value: stats.confirmed,  color: '#3B82F6',        icon: <HourglassEmptyIcon /> },
          { label: 'Atendidos',             value: stats.completed,  color: 'success.main',  icon: <CheckCircleIcon /> },
          { label: 'Cancelados/No asistió', value: stats.cancelled,  color: 'error.main',    icon: <CancelIcon /> },
        ].map((s) => (
          <Grid
            key={s.label}
            size={{
              xs: 6,
              sm: 3
            }}>
            <Paper sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ color: s.color }}>{s.icon}</Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
        {/* Próximas citas chip */}
        {upcomingSoon.length > 0 && (
          <Grid
            size={{
              xs: 12,
              sm: "auto"
            }}>
            <Chip
              label={`⏰ ${upcomingSoon.length} próxima${upcomingSoon.length > 1 ? 's' : ''} (≤30 min)`}
              color="warning" variant="filled" size="small"
              icon={<NotificationsActiveIcon sx={{ fontSize: 14 }}/>}
              sx={{ fontWeight: 700, cursor: 'pointer',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.7 } },
                animation: 'pulse 1.5s ease-in-out infinite' }}
              onClick={() => {
                const hour = parseInt((upcomingSoon[0].time || '').split(':')[0]);
                if (dayViewRef.current) dayViewRef.current.scrollTop = Math.max(0, (hour - WORK_HOURS[0]) * HOUR_HEIGHT);
                setView('day');
              }}/>
          </Grid>
        )}
      </Grid>
      {/* ── Main panel ── */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>

        {/* ── Toolbar ── */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', bgcolor: 'white' }}>
          {/* Nueva Cita */}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setQuickPickDate(toDateStr(selectedDate));
              setQuickPickTime('');
              setQuickPickProf('');
              setQuickPickSobrecupo(false);
              setQuickPickOpen(true);
            }}
            sx={{ borderRadius: 3, fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Nueva Cita
          </Button>

          {/* Navigation */}
          {view !== 'list' && (
            <>
              <Tooltip title="Hoy"><IconButton onClick={navToday} size="small"><TodayIcon /></IconButton></Tooltip>
              <IconButton onClick={navPrev} size="small"><ChevronLeftIcon /></IconButton>
              <IconButton onClick={navNext} size="small"><ChevronRightIcon /></IconButton>
            </>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1, flex: isMobile ? '1 1 100%' : 'initial' }}>
            {headerLabel}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterListIcon color="action" fontSize="small" />
            {isVeterinario || isDirectorMedico ? (
              <Chip
                label={branches.find((b) => b.id === filterBranch)?.name || accessibleBranches.find((b) => b.id === filterBranch)?.name || 'Sucursal'}
                size="small"
                color="primary"
                variant="outlined"
                icon={<StorefrontIcon sx={{ fontSize: 16 }} />}
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <TextField select size="small" value={filterBranch} onChange={(e) => { setFilterBranch(e.target.value); setFilterClinic(''); }}
                label="Sucursal" sx={{ minWidth: 140 }} SelectProps={{ displayEmpty: true }}>
                {isAdmin && <MenuItem value="">Todas</MenuItem>}
                {(isAdmin ? branches : accessibleBranches).map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </TextField>
            )}
            <SearchableSelect size="small" fullWidth={false} value={filterClinic} onChange={(e) => setFilterClinic(e.target.value)}
              label="Clínica" sx={{ minWidth: 140 }} emptyLabel="Todas"
              options={filteredClinics.map((c) => ({ value: c.id, label: c.name }))} />
            {isVeterinario ? (
              <Chip
                label={`Dr/a. ${professionals.find(p => p.id === filterProf)?.nombre || ''} ${professionals.find(p => p.id === filterProf)?.apellidos || ''}`.trim()}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <SearchableSelect size="small" fullWidth={false} value={filterProf} onChange={(e) => setFilterProf(e.target.value)}
                label="Profesional" sx={{ minWidth: 160 }} emptyLabel="Todos"
                options={profDropdownOptions.map((p) => ({
                  value: p.id,
                  label: `Dr/a. ${p.nombre} ${p.apellidos || ''}`.trim(),
                }))} />
            )}
            <SearchableSelect size="small" fullWidth={false} value={filterSpec} onChange={(e) => setFilterSpec(e.target.value)}
              label="Especialidad" sx={{ minWidth: 140 }} emptyLabel="Todas"
              options={specialties.map((s) => ({ value: s.id, label: s.name }))} />
            <SearchableSelect size="small" fullWidth={false} value={filterCapacity} onChange={(e) => setFilterCapacity(e.target.value)}
              label="Consulta" sx={{ minWidth: 140 }} emptyLabel="Todas"
              options={filteredCapacities.map((c) => ({ value: c.id, label: String(c.number) }))} />
            <TextField select size="small" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              label="Estado" sx={{ minWidth: 130 }} SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
            {view === 'day' && (
              <TextField size="small" placeholder="Buscar cita (tutor, mascota, RUT)"
                value={daySearch} onChange={(e) => setDaySearch(e.target.value)}
                sx={{ minWidth: 220 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                }} />
            )}
          </Box>

          {/* Clear all filters */}
          {( (isAdmin && filterBranch) || filterClinic || (!isVeterinario && filterProf) || filterSpec || filterCapacity || filterStatus) && (
            <Tooltip title="Limpiar todos los filtros">
              <Chip
                label="Limpiar filtros"
                size="small"
                onDelete={() => {
                  // Para no-admin la sucursal es bloqueada: volver al valor por defecto, nunca vacío.
                  setFilterBranch(isAdmin ? '' : (medicoBranchId || activeBranchId || accessibleBranches[0]?.id || ''));
                  setFilterClinic('');
                  setFilterProf(isVeterinario ? (userData?.professionalId || '') : '');
                  setFilterSpec('');
                  setFilterCapacity('');
                  setFilterStatus('');
                }}
                deleteIcon={<ClearIcon />}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          )}

          {/* View switcher */}
          <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', ml: 1 }}>
            {[
              { v: 'day',   label: 'Día',    icon: <ViewDayIcon  fontSize="small" /> },
              { v: 'week',  label: 'Semana', icon: <ViewWeekIcon fontSize="small" /> },
              { v: 'month', label: 'Mes',    icon: <CalendarMonthIcon fontSize="small" /> },
              { v: 'list',  label: 'Lista',  icon: <ListAltIcon  fontSize="small" /> },
            ].map(({ v, label, icon }) => (
              <Tooltip title={label} key={v}>
                <IconButton
                  size="small" onClick={() => setView(v)}
                  sx={{ borderRadius: 0, px: 1.5, bgcolor: view === v ? 'primary.main' : 'transparent', color: view === v ? 'white' : 'action.active', '&:hover': { bgcolor: view === v ? 'primary.dark' : 'grey.100' } }}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ bgcolor: 'white', maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : view === 'day' ? renderDayView()
            : view === 'week' ? renderWeekView()
            : view === 'month' ? renderMonthView()
            : renderListView()}
        </Box>
      </Paper>
      {renderBookingDialog()}
      {renderDetailDialog()}
      {reagendarDialog}
      {reassignDialog}
      {paymentOpen && payingBooking && (
        <PaymentDialog
          open={paymentOpen}
          onClose={() => { setPaymentOpen(false); setPayingBooking(null); }}
          onPay={handleBookingPay}
          orderNumber={payingBooking.bookingCode || ''}
          exams={[
            {
              examTypeId: payingBooking.specialtyId || 'consulta',
              examTypeName: payingBooking.specialtyName || 'Consulta',
              precioArancel: payingBooking.amount || 0,
              precioNeto: netoDesdeBruto(payingBooking.amount || 0),
            },
          ]}
        />
      )}
      <WaitlistDialog
        open={waitlistDlgOpen}
        onClose={(result) => {
          setWaitlistDlgOpen(false);
          if (result === 'success') setSnackbar({ open: true, message: 'Agregado a lista de espera. Te avisaremos si hay cupo.', severity: 'info' });
        }}
        slot={waitlistDlgData.slot}
        professional={waitlistDlgData.professional}
        date={toDateStr(selectedDate)}
        clinicId={filterClinic} specialtyId={filterSpec} capacityId={filterCapacity}
        userData={userData}
      />
      {/* ── Quick-pick: Nueva Cita ── */}
      <Dialog open={quickPickOpen} onClose={() => setQuickPickOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <Box sx={{ bgcolor: 'primary.main', px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>Nueva Cita</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Elige fecha, profesional y hora</Typography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                type="date"
                label="Fecha"
                value={quickPickDate}
                onChange={(e) => setQuickPickDate(e.target.value)}
                fullWidth size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <SearchableSelect
                label="Profesional"
                value={quickPickProf}
                onChange={(e) => setQuickPickProf(e.target.value)}
                options={professionals.map((p) => ({
                  value: p.id,
                  label: `Dr/a. ${p.nombre} ${p.apellidos || ''}`.trim(),
                }))}
                emptyLabel="Seleccionar…"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Hora (HH:MM)" value={quickPickTime}
                onChange={(e) => setQuickPickTime(e.target.value)}
                fullWidth size="small" placeholder="Ej: 09:30"
                InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={quickPickSobrecupo}
                    onChange={(e) => setQuickPickSobrecupo(e.target.checked)}
                  />
                }
                label="Citar como sobrecupo"
              />
              {quickPickSobrecupo && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1 }}>
                  Máximo 2 sobrecupos por agenda. Indica el inicio o término de un cupo disponible.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setQuickPickOpen(false)} sx={{ borderRadius: 3 }}>Cancelar</Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            sx={{ borderRadius: 3 }}
            disabled={!quickPickDate || !quickPickProf || !quickPickTime}
            onClick={() => {
              const prof = professionals.find((p) => p.id === quickPickProf);
              if (!prof) return;
              const dateObj = new Date(quickPickDate + 'T12:00:00');
              setQuickPickOpen(false);
              setView('day');
              setSelectedDate(dateObj);
              handleOpenBookDlg(dateObj, quickPickTime, prof, quickPickSobrecupo);
            }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Scheduler;
